/**
 * This module contains the main functionality of SRT, including [initializing]{@link module:core/app.init} any required modules, plus managing app [content]{@link module:core/app~content}, user journey / [path]{@link module:core/app~path}, and [settings]{@link module:core/app.settings} data.
 *
 * While this is the first step in main app functionality, the first initialization call happens in a project's entry file for the current build. Entries are located in `/src/assets/scripts/entry/`.
 *
 * @module core/app
 */

// Import everything from autoload
import './autoload/hbs.runtime.min.js'; import './autoload/polyfills.js'; import './autoload/template-helpers.js'; import './autoload/template-partials.js';

import * as Util from './util';
import * as View from './view';
import * as Analytics from './analytics';
import * as Matrix from "./matrix";
import * as Filter from "./filter";
import * as Offline from './offline';
import * as Customize from 'project/customize';

// some settings are injected during build process
// specifically, through webpack.DefinePlugin
// throw error if build settings not injected
if (typeof appData === undefined) throw 'App config not properly initialized in build process.';

/**
 * App configuration defaults.
 *
 * These values are used as the basis for the [`settings`]{@link module:core/app.settings} object, and are applied during [`settingsInit`]{@link module:core/app~settingsInit}.
 *
 * Config defaults can be updated using [`updateConfig`]{@link module:core/app.updateConfig}.
 *
 * @since 3.2.0
 *
 * @type {object}
 *
 * @see [`updateConfig`]{@link module:core/app.updateConfig}
 * @see [`settingsInit`]{@link module:core/app~settingsInit}
 */
const config = {
  allow_continue: false,
  analytics: Analytics.config,
  api_images: {
    enabled: false,
    fallback_only: false,
    dimensions: [320, 240],
  },
  app: {
    schema: {
      platform: {
        type: 'string',
      },
      app_id: {
        type: 'string',
      },
      app_vendor: {
        type: 'string',
      },
      path: {
        type: 'string',
      },
    },
  },
  apply_past_selections: false,
  aria_label: {},
  auto_continue: false,
  banner: {
    enabled: false,
  },
  debug: false,
  destinations: {},
  demo: false,
  direct_embed: {
    enabled: false,
  },
  direct_to_retailer: {
    enabled: false,
    continue: false,
  },
  dynamic_refs: {},
  embed: {
    type: 'script',
  },
  facet: {
    enabled: false,
    global: false,
    filter_on: {},
  },
  filter: Filter.config,
  format_number: {
    enabled: false,
    locale: 'en-US',
    options: {},
  },
  get_products: 0,
  hatch_button: {
    enabled: false,
    mpn: 'sku',
    ean: '',
    script: 'https://cdn.gethatch.com/hatch-buy-now-request-1.0.stable.js',
    country_code: 'us',
  },
  iframe: {},
  initial_load: true,
  labels: {
    demo_mode_dialog: 'App is in demo mode.',
    products_show_more: 'Show more results',
  },
  lang: appData.settings.langs[0],
  langs: appData.settings.langs,
  lang_content_location: 'content',
  lang_dir: 'ltr',
  lang_styles_location: 'assets/styles',
  matrix: Matrix.config,
  modal_side_journey: false,
  modal_animation_delay: 500,
  offline: Offline.config,
  offsite_notice: {
    enabled: false,
  },
  path: '',
  persistent: {
    enabled: false,
    paths: [],
  },
  scroll_to_top: true,
  scroll_offset: 0,
  share_url: (appData.settings.platform === 'iframe') ? document.referrer : window.location.href,
  syndication: {
    s3_production: 'https://intel-srt-v2.s3.us-east-2.amazonaws.com/',
    s3_staging: 'https://intel-srt-v2-dev.s3.us-east-2.amazonaws.com/',
  },
  transitions: {
    in: {
      length: 500,
      progressive: 200,
      reverse: false,
    },
    out: {
      length: 250,
      progressive: 100,
      reverse: true,
    },
  },
  util: Util.config,
  view: View.config,
};

/**
 * Contains data from a user's journey through the app, as an array of [`node`]{@link module:core/app~nodeInit} objects.
 *
 * @since 3.0.0
 *
 * @type {array}
 *
 * @see [`start`]{@link module:core/app.start}
 * @see [`forward`]{@link module:core/app.forward}
 * @see [`backward`]{@link module:core/app.backward}
 * @see [`updatePathNode`]{@link module:core/app.updatePathNode}
 * @see [`currentNodeSetting`]{@link module:core/app.currentNodeSetting}
 * @see [`previousNodeSetting`]{@link module:core/app.previousNodeSetting}
 * @see [`pathAttributes`]{@link module:core/app~pathAttributes}
 *
 * @example
 * [
 *   {
 *     location: "start",
 *     dq: [],
 *     settings: {
 *       auto_continue: true,
 *       analytics_step: 1,
 *     },
 *   },
 *   {
 *     location: "usage_models",
 *     prev_location: "start",
 *     dq: ["recommendation"],
 *     settings: {
 *       analytics_step: 2,
 *     },
 *     selections: ['input_1_id', 'input_4_id'],
 *     attributes: {
 *       analytics: { ... },
 *       filter: { ... },
 *     },
 *   },
 * ]
 */
let path = [];

/**
 * App content.
 *
 * This object represents the full app content structure, including data imported from `config.json` project files, plus any appended, modified, or otherwise updated information necessary for core or project functionality.
 *
 * Content can be retrieved from other modules with [`getContent`]{@link module:core/app.getContent}, and additional content can be added with [`appendContent`]{@link module:core/app.appendContent}.
 *
 * Information on content properties can be found in the [`Content`]{@link module:core/config} module.
 *
 * @since 3.0.0
 *
 * @type {object}
 *
 * @see [`getContent`]{@link module:core/app.getContent}
 * @see [`appendContent`]{@link module:core/app.appendContent}
 */
let content = {};

/**
 * Object representing the current state of the app, including current and previous [`path`]{@link module:core/app~path} nodes, path attributes in both uncondensed and condensed format, as well as the direction most recently traveled.
 *
 * @since 3.0.0
 *
 * @type {object}
 *
 * @example
 * {
 *   path_depth: 0,
 *   direction: "forward",
 *   previous: { ... },
 *   current: { ... },
 *   attributes: {
 *     app: [
 *       {
 *         name: 'platform',
 *         value: 'online',
 *       },
 *     ],
 *   },
 *   attr: {
 *     app: {
 *       platform: 'online',
 *     },
 *   },
 * }
 *
 * @see [`updateState`]{@link module:core/app.updateState}
 */
export let state = {};

/**
 * Prepared content for use in the current view.
 *
 * Contains data from [`content`]{@link module:core/app~content} for a particular location, plus any injected content from core or project.
 *
 * @since 3.0.0
 *
 * @type {object}
 *
 * @see [`setCurrentContent`]{@link module:core/app~setCurrentContent}
 */
export let current = {};

/**
 * The primary settings object for the app.
 *
 * Settings are a combination of app and module default [`config`]{@link module:core/app~config}, `[content]{@link module:core/app~content}.settings` from content JSON, build settings included from the build process, and any additional settings updated from elsewhere in core or project.
 *
 * More information on settings can be found in [`Config.settings`]{@link module:core/config.settings}.
 *
 * @since 3.0.0
 *
 * @type {object}
 *
 * @see [`settingsInit`]{@link module:core/app~settingsInit}
 * @see [`updateSettings`]{@link module:core/app.updateSettings}
 * @see [`Config.settings`]{@link module:core/config.settings}
 */
export let settings = {};

/**
 * Hold project attribute definitions. Definitions can be added or updated with [`updateAttributeDefs`]{@link module:core/app.updateAttributeDefs}.
 *
 * Attribute definitions contain information on how a given attribute's data should be handled throughout the app. An attribute's definition object is often passed around as a parameter while processing its data to ensure it is properly stored and interpreted.
 *
 * #### Definition object properties:
 *
 * - `type` - Name of the attribute type. This is the property name that attributes will be accessible as in [`state`]{@link module:core/app.state}.
 * - `allowMultiple` - Whether the [condensed attribute properties]{@link module:core/util.condenseAttributes} should be stored as an array, or as single values.
 * - `allowUnrecognized` - When enabled, attributes with unrecognized schema or type definitions will be kept during [formatting]{@link module:core/util.formatAttributes}. If disabled, they are deleted. *Note that this only applies to attribute types that have a schema definition - if no schema is provided, all attributes are always kept.*
 * - `primary` - An attribute defined as a "primary" will be used as the default attribute in certain methods. This attribute is stored in settings as `settings.primary_attribute`.
 * - `ignoreAnchor` - When enabled, attributes of this type will not be anchored during [`View.setActiveSelections`]{@link module:core/view~setActiveSelections} even when the `anchor_path_attributes` [page setting]{@link module:core/config.page_settings} is enabled.
 * - `applyActive` - When enabled, attributes present in the app path will be used to activate selections with matching attributes, when `apply_past_selections` [page setting]{@link module:core/config.page_settings} is enabled.
 *
 * Only defined [`attributes`]{@link module:core/config.attributes} will be stored in the app when running [`setAttributes`]{@link module:core/app.setAttributes}, so any project-specific attributes will need to be defined before they can be used as part of [conditionals]{@link module:core/config.conditional_on} or custom recommendation functionality.
 *
 * @since 3.0.0
 *
 * @type {array}
 *
 * @see [`updateAttributeDefs`]{@link module:core/app.updateAttributeDefs}
 * @see [`setAttributes`]{@link module:core/app.setAttributes}
 *
 * @example
 * // attribute defaults
 * [
 *   {
 *     type: 'app',
 *     ignoreAnchor: true,
 *   },
 *   {
 *     type: 'filter',
 *     allowMultiple: true,
 *     applyActive: true,
 *     primary: true,
 *   },
 *   {
 *     type: 'analytics',
 *     allowMultiple: true,
 *   },
 * ]
 */
export const attributes = [
  {
    type: 'app',
    ignoreAnchor: true,
  },
  {
    type: 'filter',
    allowMultiple: true,
    applyActive: true,
    primary: true,
  },
  {
    type: 'analytics',
    allowMultiple: true,
  },
];

/*
METHODS
*/

/**
 * Initialize app.
 *
 * - [App config is initialized]{@link module:core/app~configInit}.
 * - [App content is initialized]{@link module:core/app.contentInit}.
 * - [App settings are initialized]{@link module:core/app~settingsInit}.
 * - App modules are initialized, in this order:
 *     - [View init]{@link module:core/view.init}.
 *     - [Analytics init]{@link module:core/analytics.init}.
 *     - Optional modules passed in through `optModules` param.
 *     - If matrix build, [Matrix init]{@link module:core/matrix.init}.
 *     - Project init, through the [`customize`]{@link module:core/app.customize} method.
 *     - [Filter init]{@link module:core/filter.init}.
 * - App journey is started by [navigating to starting view]{@link module:core/view.navigate}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} [entryConfig] - Configuration settings passed in by entry.
 *
 * @param {...function} [optModules] - Optional module init functions passed in by entry.
 *
 * @see [`configInit`]{@link module:core/app~configInit}
 * @see [`contentInit`]{@link module:core/app.contentInit}
 * @see [`settingsInit`]{@link module:core/app~settingsInit}
 * @see [`View.init`]{@link module:core/view.init}
 * @see [`Analytics.init`]{@link module:core/analytics.init}
 * @see [`Matrix.init`]{@link module:core/matrix.init}
 * @see [`customize`]{@link module:core/app.customize}
 * @see [`Filter.init`]{@link module:core/filter.init}
 * @see [`View.navigate`]{@link module:core/view.navigate}
 */
export const init = (entryConfig = {}, ...optModules) => {
  // initialize content
  configInit(entryConfig)
    .then(() => contentInit())
    .then(() => settingsInit())
    .then(() => {
      // initialize view
      View.init();
      // initialize analytics
      Analytics.init();
      // initialize analytics
      Offline.init();
      // initialize any optional modules provided by entry
      for (let moduleInit of optModules) moduleInit();
      // initialize project-specific code
      customize('init');
      // initialize filter, then start app
      return Filter.init();
    })
    .then(() => {
      // start view
      View.navigate();
    })
    .catch((e) => Util.logError(e, 'App failed to start.'));
};

/**
 * Initialize language-specific JSON content, if it exists, and run some actions once loaded.
 *
 * 1. AJAX load content for the provided `lang`.
 * 1. Set [`content`]{@link module:core/app~content} to (cloned) app config content. If `lang`-specific JSON was loaded, merge it in.
 * 1. If config `lang` is different than the currently requested `lang`, [update config]{@link module:core/app.updateConfig} with new value.
 *
 * Returns promise once complete.
 *
 * **Note that if no JSON for the current language is provided (i.e., content labels are included in app config), a 404 error will be thrown. This is expected and is not indicative of any issue that needs correcting.**
 *
 * @method
 *
 * @since 3.2.0
 *
 * @param {string} lang - Language code to initialize, e.g. `en`, `fr`. Default: `config.lang`
 *
 * @return {Promise}
 *
 * @see [`updateConfig`]{@link module:core/app.updateConfig}
 * @see Used by:
 * @see [**`init`**]{@link module:core/app.init}
 */
export const contentInit = (lang = config.lang) => {
  let langContent = {};
  // get lang content
  return $.ajax({
    dataType: `json`,
    url: `${config.lang_content_location}/${lang}.json`,
  })
    // if lang json was retrieved, set langContent to it
    .then((json) => {
      langContent = json;
    }, () => {
      console.log('No language content found. This notice can be ignored if labels are included in app content.');
    })
    .always(() => {
      content = Util.merge(Util.cloneDeep(appData), langContent);
      // update config with the current lang
      if (config.lang !== lang) updateConfig({lang});
    })
};

/**
 * Sets up initial [`path`]{@link module:core/app~path} nodes, including any data present in querystring in dev mode.
 *
 * The app typically starts from the location set in `settings.start`. However, if the app is reloaded in dev mode (e.g. in dev environments, or if `settings.dev_mode` is manually enabled), the app will attempt to start at the last active view. This enables easier debugging or styling a specific layout/page without needing to click through the path to reach it.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} [location] - View ID to start app from.
 *
 * @see [`clearDeepLink`]{@link module:core/app.clearDeepLink}
 * @see [`pathInit`]{@link module:core/app~pathInit}
 * @see [`setAttributes`]{@link module:core/app.setAttributes}
 * @see [`postNavigate`]{@link module:core/app~postNavigate}
 * @see Used by:
 * @see [**`View.navigate`**]{@link module:core/view.navigate}
 * @see [**`Matrix.walk`**]{@link module:core/matrix.walk}
 */
export const start = (location) => {
  let query = Util.getURLParam();
  // determine if a dev start should be run
  // dev start checks for existing parameters like location and filter selections in a query string
  let devStart = settings.dev_mode && query && query.location && !location;
  //let devStart = settings.dev_mode && query && query.p && !location;

  let shareStart = query && query.srt_share;

  // if dev mode, check if there is a query location to start from
  if (devStart) {
    location = query.location;
    // location = Util.decode(query.p)[1];
    // if location is view all, initialize view all content
    if (location === 'all_inventory') viewAllInit();
    // remove location from query
    delete query.location;
    //delete query.p;
  }
  // if opening a share link
  else if (shareStart) location = settings.end;
  // otherwise use start
  else if (!location) location = settings.start;

  // if the location does not exist, fail
  if (!content[location]) throw `No destination by the name "${location}" exists.`;

  // begin path
  pathInit(location, devStart, shareStart, query);
  // clear state
  state = {};
  // set initial attributes state
  setAttributes();
  // post-navigation processing
  postNavigate('start');
};

/**
 * Advance internal app state to a new destination.
 *
 * The next destination will always be the first item in the current node's destination queue, or a provided destination view ID. A new node is created using the new destination view's location data and added to the [`path`]{@link module:core/app~path}, then [post-navigation processing]{@link module:core/app~postNavigate} is handled.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} [destination] - Optional destination view ID for out-of-queue navigation (e.g. side journeys).
 *
 * @see [`path`]{@link module:core/app~path}
 * @see [`nodeInit`]{@link module:core/app~nodeInit}
 * @see [`postNavigate`]{@link module:core/app~postNavigate}
 * @see Used by:
 * @see [**`View.navigate`**]{@link module:core/view.navigate}
 * @see [**`Matrix.forward`**]{@link module:core/matrix~forward}
 */
export const forward = (destination = '') => {
  // set next destination
  let location = destination || state.current.dq.shift();

  // if the location does not exist, fail
  if (!content[location]) throw `No destination by the name "${location}" exists.`;

  // create new node and add to journey path
  // note that the "current" location is passed to the "previous" location arg, since the path has not yet been updated
  path.push(nodeInit({
    location: location,
    prev_location: state.current.location,
  }));

  // post-navigation processing
  postNavigate('forward');
};

/**
 * Navigate the internal app state back to the previous node. Removes the last node in [`path`]{@link module:core/app~path}, then handles [post-navigation processing]{@link module:core/app~postNavigate}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`path`]{@link module:core/app~path}
 * @see [`postNavigate`]{@link module:core/app~postNavigate}
 * @see Used by:
 * @see [**`View.navigate`**]{@link module:core/view.navigate}
 */
export const backward = () => {
  // if no previous view is found, just restart
  // this can happen when reloading a dev build in the middle of a journey
  if (!state.current.prev_location) {
    View.navigate('restart');
    return;
  }
  // previous location must be unique
  if (state.previous.location !== state.current.location) {
    // remove last location from array
    path.pop();

    // post-navigation processing
    postNavigate('back');
  } else {
    throw `Previous location is identical to current location.`
  }
};

/**
 * Add arbitrary data to a path node. See [`nodeInit`]{@link module:core/app~nodeInit} for more information on node structure.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} data - Data to add into the specified path node.
 * @param {boolean} [merge] - When enabled, provided data will be [merged]{@link module:core/util.mergeAll} (including concatenating arrays) into node. By default, `Object.assign` will be used to overwrite existing properties. Default: `false`
 * @param {number} [index] - Index of the path node to update. An index of `-1` will use the current path node. Default: `-1`
 *
 * @see Used by:
 * @see [**`setAttributes`**]{@link module:core/app.setAttributes}
 * @see [**`getProducts`**]{@link module:core/app~getProducts}
 * @see [**`View.tabs.open`**]{@link module:core/view.tabs.open}
 * @see [**`View.updateState`**]{@link module:core/view.updateState}
 */
export const updatePathNode = (data, merge = false, index = -1) => {
  // if no index is passed in, set index to current path node index
  if (index === -1) index = path.length - 1;
  // define update method
  let update = merge ? Util.mergeAll : Object.assign;
  // add data to path node
  update(path[index], data);
};

/**
 * Add to or update data in the current [`state`]{@link module:core/app.state}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} data - Data to add to app state.
 * @param {boolean} [merge] - When enabled, provided data will be [merged]{@link module:core/util.merge} into `state` object. By default, `Object.assign` will be used to overwrite existing properties. Default: `false`
 *
 * @see Used by:
 * @see [**`setAttributes`**]{@link module:core/app.setAttributes}
 * @see [**`postNavigate`**]{@link module:core/app~postNavigate}
 *
 * @example
 * // beginning state
 * {
 *   attributes: {
 *     form_factor: "notebook",
 *   },
 * }
 *
 * // data to add
 * let data = {
 *   attributes: {
 *     os: "windows",
 *   },
 * }
 *
 * @example
 * // assign
 * updateState(data);
 *
 * // expected state
 * {
 *   attributes: {
 *     os: "windows",
 *   },
 * }
 *
 * @example
 * // merge
 * updateState(data, true);
 *
 * // expected state
 * {
 *   attributes: {
 *     form_factor: "notebook",
 *     os: "windows",
 *   },
 * }
 */
export const updateState = (data, merge = false) => {
  // set combine function
  const combine = merge ? Util.merge : Object.assign;
  // combine state and data
  state = combine(state, data);
};

/**
 * Add or update config defaults. Provided objects are [merged]{@link module:core/util.merge} into app [`config`]{@link module:core/app~config}.
 *
 * @method
 *
 * @since 3.2.0
 *
 * @param {...object} data - Data to add to app config. See [`updateSettings`]{@link module:core/app.updateSettings} for merging examples.
 *
 * @see Used by:
 * @see [**`configInit`**]{@link module:core/app~configInit}
 *
 */
export const updateConfig = (...data) => Util.merge(config, ...data);

/**
 * Add or update settings. Should only be used to add or update **global** app settings.
 *
 * - Provided objects are [merged]{@link module:core/util.merge} into app [`settings`]{@link module:core/app.settings}.
 * - [Persistent settings are stored]{@link module:core/app~updatePersistentSettings}, if applicable.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {...object} data - Data to add to app settings.
 *
 * @example
 * // initial settings state
 * "settings": {
 *   "static_setting": false,
 *   "existing_setting": 0
 * }
 *
 * let modifySettings = {
 *   new_setting: "example",
 *   existing_setting: 5,
 * }
 *
 * updateSettings(modifySettings);
 *
 * // expected final settings:
 * "settings": {
 *   "static_setting": false,
 *   "existing_setting": 5,
 *   "new_setting": "example"
 * }
 *
 * @see [`updatePersistentSettings`]{@link module:core/app~updatePersistentSettings}
 * @see Used by:
 * @see [**`settingsInit`**]{@link module:core/app~settingsInit}
 * @see [**`Matrix.init`**]{@link module:core/matrix.init}
 * @see [**`Offline.init`**]{@link module:core/offline.init}
 */
export const updateSettings = (...data) => {
  Util.merge(settings, ...data);
  updatePersistentSettings();
}

/**
 * Update [product]{@link module:core/app~getProducts} and [view]{@link module:core/app~getProducts} content. Useful for updating the view content from another module, when navigation has not occurred.
 *
 * @method
 *
 * @see [`getProducts`]{@link module:core/app~getProducts}
 * @see [`setCurrentContent`]{@link module:core/app~getProducts}
 * @see Used by:
 * @see [**`postNavigate`**]{@link module:core/app~postNavigate}
 */
export const updateContent = () => {
  // check for product recommendations
  getProducts();
  // update current content
  setCurrentContent();
};

/**
 * Get previous destination queue, and return updated queue with new unique destinations.
 *
 * The destination queue is an array of view IDs representing views to visit, in the order listed in the array. Queues are stored in and updated for each [`path`]{@link module:core/app~path} node during [`View.updateNodeState`]{@link module:core/view.updateNodeState}. If the previous node had an existing queue, then any destination selections made in the current view will be added (if not already present) and stored in the current node.
 *
 * If needed, a destination can also be forced to the front of the queue using the `forceToFront` param. This can be used when it is necessary to circumvent the existing destination queue, like in the case of navigating to a "side journey" view that is out of the main app flow.
 *
 * More information on using destination queue to stack multiple destinations can be found in [`options`]{@link module:core/config.options} content documentation under `destination_priority`.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array} destinations - Array of "destinations" or view ID values.
 * @param {boolean} forceToFront - When `true`, pushes provided destinations to the front of the queue, without checking for existing locations.
 *
 * @returns {Array}
 *
 * @see Used by:
 * @see [**`View.updateNodeState`**]{@link module:core/view.updateNodeState}
 *
 * @example
 * // previous node queue
 * {
 *   dq = ['page_2', 'page_3', 'page_5'],
 * }
 *
 * // destinations selected in current view
 * let newSelections = ['page_3', 'recommendation'];
 *
 * // update queue
 * getDestinationQueue(newSelections);
 *
 * // expected result:
 * ['page_2', 'page_3', 'page_5', 'recommendation']
 *
 * // the next view to be visited will be "page_2"
 */
export const getDestinationQueue = (destinations, forceToFront = false) => {
  // if destination is not an array, throw error.
  if (!Array.isArray(destinations)) throw 'App.getDestinationQueue requires an array of destinations.';
  // get current destination queue from path node
  let currentQueue = state.previous.dq.slice();
  // if forcing destination(s) to the front, just add to beginning of queue
  if (forceToFront) currentQueue.unshift(...destinations);
  // otherwise, add destinations to current queue, if they don't already exist
  else Util.arrayPushUnique(currentQueue, ...destinations);

  return currentQueue;
};

/**
 * Get the value of a given setting property from the current node. Note that only settings from the node itself, not global [`settings`]{@link module:core/app.settings}, are checked.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} settingName - Property name of the desired setting.
 * @param {*} [defaultValue] - Provide a fallback default value, to return if no setting value is found. Default: `false`
 *
 * @returns {*}
 *
 * @example
 * // check "get_products" setting, falling back to a value of -1 if setting is not present
 * let productCount = currentNodeSetting('get_products', -1);
 */
export const currentNodeSetting = (settingName, defaultValue = false) => {
  let settingValue = defaultValue;
  if (state.current.settings && state.current.settings.hasOwnProperty(settingName)) {
    settingValue = state.current.settings[settingName];
  }
  return settingValue;
};

/**
 * Get the value of a given setting property from the previous node. Note that only settings from the node itself, not global [`settings`]{@link module:core/app.settings}, are checked.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} settingName - Property name of the desired setting.
 * @param {*} [defaultValue] - Provide a fallback default value, to return if no setting value is found. Default: `false`
 *
 * @returns {*}
 *
 * @example
 * // check referring view's "recommend_lowest_price" setting, falling back to a value of "true" if setting is not present
 * let useLowestPrice = previousNodeSetting('recommend_lowest_price', true);
 */
export const previousNodeSetting = (settingName, defaultValue = false) => {
  let settingValue = defaultValue;
  if (state.previous && state.previous.settings && state.previous.settings.hasOwnProperty(settingName)) {
    settingValue = state.previous.settings[settingName];
  }
  return settingValue;
};

/**
 * Get [content]{@link module:core/app~content} for a given view ID. Returns `false` if no content is found at the requested location.
 *
 * Note that content is deep-cloned to prevent changes to original content object.
 *
 * @method
 *
 * @since 3.1.1
 *
 * @param {string} viewID - View ID to retrieve content for.
 *
 * @return {object|boolean}
 */
export const getContent = (viewID) => content[viewID] ? Util.cloneDeep(content[viewID]) : false;

/**
 * Append new view content on to [`content`]{@link module:core/app~content} object.
 *
 * By default, content will only be added if there is no existing data at the provided view ID. However, this behavior can be overridden with the `force` parameter, in which case provided content will replace any existing content.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} key - Content location ID.
 * @param {object} contents - Content to add to this location.
 * @param {boolean} [force] - Override existing content, if it exists in this location. Default: `false`
 *
 * @example
 * // add "view all" page data
 * let viewAll = {
 *   "title": "View All Inventory",
 *   "content": [
 *     ...
 *   ]
 * }
 *
 * appendContent(viewAll);
 */
export const appendContent = (key, contents, force = false) => {
  if (!content[key] || force) {
    content[key] = contents;
  }
};

/**
 * Check a set of condition objects against a collection of attribute types. Returns a boolean value representing whether the provided conditions have been met.
 *
 * By default, conditions are tested against the current view's condensed attributes, or `[state]{@link module:core/app.state}.current.attr`. If the `global` param is enabled, global condensed attributes will be used, or `[state]{@link module:core/app.state}.attr`. Alternatively, custom attributes object can be provided to test against.
 *
 * @method
 *
 * @since 3.1.0
 *
 * @param {array} conditions - Array of [`conditional_on`]{@link module:core/config.conditional_on} conditions to check against attributes collection.
 * @param {boolean} global - Whether conditions should be tested against global or view attributes. If using custom `attr`, this param is ignored.
 * @param {object} attr - Attributes object. Includes condensed attributes for any attribute type that should be used to test conditions against. See [`conditionMatch`]{@link module:core/app.conditionMatch} for more information on valid attributes objects.
 *
 * @see [`conditionMatch`]{@link module:core/app.conditionMatch}
 * @see Used by:
 * @see [**`transformContent`**]{@link module:core/app.transformContent}
 */
export const conditionalOn = (conditions, global = false, attr = {}) => {
  // if attributes are not provided, set attr to either view or global attributes.
  if (!Object.keys(attr).length) attr = global ? state.attr : state.current.attr;
  // if no conditions are provided, consider conditions met
  if (!conditions || !conditions.length) return true;
  // determine if conditions are met
  // reduces conditions array down to a single match result
  // initial state is true since all conditions must be met
  return conditions.reduce((match, condition) => {
    // set condition to an array, if it isn't already
    condition = Array.isArray(condition) ? condition : [condition];
    // remove out-of-scope conditions, either global or non-global depending on global param
    condition = condition.filter(obj => !!obj.global === global);
    // if conditions remain, check for match, otherwise condition is met for now
    let result = condition.length ? conditionMatch(condition, attr) : true;
    // if current condition is not met, return false, otherwise return either the current match (to avoid overwriting a previously failed match state)
    return result ? match : false;
  }, true);
};

/**
 * Check an individual condition or array of "OR" conditions against provided attributes.
 *
 * If multiple attribute types are to be tested (e.g., if a `type` property is included in the condition), the object may contain condensed attributes for each type. If no type is provided by the condition object, condensed attributes of a single type may be provided.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array|object} condition - An individual [`conditional_on`]{@link module:core/config.conditional_on} condition object, or an array of condition objects. If an array of conditions is provided, they will be treated as an "OR" compare.
 * @param {object} attr - An object that contains condensed attributes of all currently available types that should be used to test condition.
 *
 * @return {boolean}
 *
 * @see Used by:
 * @see [**`conditionalOn`**]{@link module:core/app.conditionalOn}
 * @see [**`View.handleConditionals`**]{@link module:core/view.handleConditionals}
 *
 * @example
 * // conditions with a type specified
 * const conditionShowIfNotebook = {
 *   "type": "filter",
 *   "name": "form_factor",
 *   "value": "notebook"
 * }
 *
 * const conditionShowIfNotNotebook = {
 *   "type": "filter",
 *   "name": "form_factor",
 *   "value": "notebook",
 *   "unless": true
 * }
 *
 * // attributes object
 * const attributes = {
 *   filter: {
 *     form_factor: "notebook",
 *   },
 *   app: {
 *     platform: "online",
 *   },
 * };
 *
 * conditionMatch(conditionShowIfNotebook, attributes);
 * // expected result: true
 *
 * conditionMatch(conditionShowIfNotNotebook, attributes);
 * // expected result: false
 *
 * @example
 * // condition with no type specified
 * const conditionShowIfNotebook = {
 *   "name": "form_factor",
 *   "value": "notebook"
 * }
 *
 * // attributes object
 * const attributes = {
 *   form_factor: "notebook",
 * };
 *
 * conditionMatch(showIfFormFactorNotebook, attributes);
 * // expected result: true
 */
export const conditionMatch = (condition, attr) => {
  // if condition is an array of conditions, treat as "OR" compare
  if (Array.isArray(condition)) return !!condition.reduce((match, obj) => conditionMatch(obj, attr) ? true : match, false);
  // request value is condition value, or if not provided, just check if item attribute value exists
  let request = condition.hasOwnProperty('value') ? condition.value : true;
  // if condition provides a type, set attributes to the requested type
  if (condition.type) attr = attr[condition.type];
  // get item value from provided attributes
  let item = attr[condition.name];
  // get match result
  let result = attributeMatch(condition.name, request, item, condition);

  // return result, inverting if necessary
  return condition.unless ? !result : !!result;
};

/**
 * Check whether an item attribute value should be considered a match for a given request attribute value(s).
 *
 * A "match" in this method means a positive result, in which the item should not be excluded from the current request, or would be given a full score weight, depending on the filter method. It does not necessarily mean that request and item attributes are identical, just that it meets the criteria to be included.
 *
 * Matches are determined differently depending on the attribute type and request. See `request` param for more information.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} attribute - The name of the attribute being matched.
 *
 * @param {string|number|boolean|array} request - Attribute value(s) to look for.
 *
 * Note that the request may be provided as an array of any of the below values, **as long as `args.allowMultiple` is enabled.** Arrays of values are processed recursively, and will return a positive match as long as at least one request value match is found (e.g. an "OR" operator).
 *
 * ---
 *
 * Acceptable request values for each attribute `type`:
 *
 * - `type: "string"`
 *     - `storage_type: "ssd"` Includes any item containing this attribute value
 *     - `storage_type: ["ssd", "hdd"]` Includes any item containing EITHER attribute value ("or" operation)
 *     - `storage_type: "hdd ssd"` Includes any item containing BOTH values ("and" operation)
 *     - `storage_type: true` Includes item if any value exists for this attribute
 * - `type: "boolean"`
 *     - `touchscreen: true` Includes item if any value exists for this attribute
 * - `type: "number"`
 *     - `storage_amount: 500` Includes any item containing this attribute value
 *     - `storage_amount: [500]` If an array of a single value is provided, the attribute's `compare` setting will be used to
 *     - `storage_amount: [250, 500, 1000]` If multiple values are provided, the array will be converted to a range containing the highest and lowest values. Any value falling between this range will be considered a match.
 *     - `storage_amount: true`  Includes item if any value exists for this attribute
 *
 * @param {string|number|boolean|array} value - Attribute value that may be a match.
 *
 * @param {object} [args] - [Attribute]{@link module:core/app.attributes} definition to use to determine matches.
 *
 * @param {string} args.type - Attribute type name. Default: [primary attribute]{@link module:core/app~getPrimaryAttribute} `type`
 *
 * @param {boolean} [args.allowMultiple] - Whether `request` property values are supplied as arrays or not. Default: `false`
 *
 * @return {boolean}
 */
export const attributeMatch = (attribute, request, value, args = {}) => {
  // define default args
  let options = Object.assign({
    type: settings.primary_attribute.type,
    allowMultiple: false,
  }, args);

  // if value is undefined, and request value is not false, return negative match
  // if request value is false, returns true
  if (value === undefined) return request === false;

  // if request attribute is a boolean "true", return boolean cast of value
  // basically, return true if any value exists for value
  if (request === true) return !!value;

  // if allowMultiple is enabled, check if any of the values are a match (equivalent to an "OR" compare).
  // note that allowMultiple is disabled for recursive runs, so attributes are treated as individuals
  if (options.allowMultiple && Array.isArray(request)) return request.reduce((m, r) => attributeMatch(attribute, r, value, {type: options.type, allowMultiple: false}) ? true : m, false);

  // if attribute is defined in schema, use schema compare, otherwise use default `is` compare
  let compare = Util.getSchemaValue('compare', attribute, options) || 'is';

  // if attribute is defined in schema, use schema type, otherwise use default `string` type
  let type = Util.getSchemaValue('type', attribute, options);

  // check number ranges
  if (type === 'number' && Array.isArray(request)) {
    // if array is already a range, return test result
    if (Util.isRange(request)) return Util.inRange(value, request);
    // otherwise convert to a range and test
    let range = {};
    // if an array of one value is requested, use compare type to convert to appropriate range
    if (request.length === 1) {
      // if compare type is "is", throw error
      if (compare === 'is') {
        console.log({request, value});
        throw 'Attribute request invalid for compare type. Use min/max as attribute schema compare type, or pass as a non-array value.';
      }
      // set either min or max to current value, depending on compare type
      range[compare] = request[0];
      // set the other value to either 0 or Infinity
      compare === 'min' ? range.max = Infinity : range.min = 0;
    }
    // if multiple values are provided, create range from highest and lowest value
    else if (request.length > 1) {
      range.max = Math.max(...request);
      range.min = Math.min(...request);
    }
    // if an empty array is provided for some reason, return false
    else return false;
    // check if in range and return result
    return Util.inRange(value, [range.min, range.max]);
  }

  // first check if items are equivalent
  if (request === value) return true;
  // if value is a non-array object, consider it a match if the request value(s) are valid properties
  if (!Array.isArray(value) && typeof value === 'object') {
    // set request to an array to test multiple values, if present
    request = Array.isArray(request) ? request : [request];
    // check that all request values are valid object property keys
    return Util.arrayIncludes(Object.keys(value), ...request);
  }

  // always use value attribute as an array of values
  value = Array.isArray(value) ? value : [value];
  // if request attribute is an array, any value within array can be a match ("or" operation)
  if (Array.isArray(request)) return request.reduce((match, attr) => Util.arrayIncludes(value, attr) ? true : match, false);

  // split single value by spaces for multiple values, wrap other types in an array
  request = typeof request === 'string' ? request.split(' ') : [request];

  // treat request attribute like an "and" operation - all request values must be present
  return Util.arrayIncludes(value, ...request);
};

/**
 * For a given attributes condition request object, check if an attributes object satisfies all conditions. Uses [`attributeMatch`]{@link module:core/app.attributeMatch} to check each request attribute value against the comparison object attribute value.
 *
 * Note that if attribute values are supplied as arrays (i.e. `allowMultiple` arg is enabled), the attribute object value being tested only needs to match one of the values to be considered a match.
 *
 * @method
 *
 * @since 3.2.3
 *
 * @param {object} request - Attributes conditions to satisfy.
 *
 * @param {object} attrObject - Object with attributes that may satisfy `request` conditions.
 *
 * @param {object} [args] - See [`attributeMatch`]{@link module:core/app.attributeMatch} `args` param.
 *
 * @return {boolean}
 *
 * @see [`attributeMatch`]{@link module:core/app.attributeMatch}
 * @see Used by:
 * @see [**`View.applyPastSelections`**]{@link module:core/view~applyPastSelections}
 * @see [**`View.setDTRLink`**]{@link module:core/view~setDTRLink}
 *
 * @example
 * // request objects
 * let request1 = {
 *   processor: 'i3',
 *   ram: 32,
 * };
 *
 * let request2 = {
 *   processor: 'i3',
 *   storage_amount: [500, 1000],
 * };
 *
 * let request3 = {
 *   processor: 'i5',
 * };
 *
 * // object that may contain attributes that match request conditions
 * let obj = {
 *   processor: 'i3',
 *   ram: 32,
 *   storage_amount: 512,
 * };
 *
 * // expected results
 * attributesMatch(request1, obj); // true
 * attributesMatch(request2, obj); // true
 * attributesMatch(request3, obj); // false
 *
 */
export const attributesMatch = (request, attrObject, args = {}) => {
  // check each request attribute for matches
  for (let [attribute, value] of Object.entries(request)) {
    // if request attribute is not present in attrObject, fail
    if (!Object.keys(attrObject).includes(attribute)) return false;
    // if attribute is not a match, fail
    if (!attributeMatch(attribute, value, attrObject[attribute], args)) return false;
  }
  // if no fail conditions were found, return true
  return true;
};

/**
 * Get attribute definition object of a given type.
 *
 * @method
 *
 * @since 3.2.3
 *
 * @param {string} type - Attribute type to retrieve.
 *
 * @return {object}
 */
export const getAttributeDef = (type) => {
  const defIndex = Util.indexOfObjectByProperty('type', type, attributes);
  if (defIndex === -1) return;
  return attributes[defIndex];
};

/**
 * Add or update app attribute definitions.
 *
 * If an attribute definition with the same type already exists, it will be replaced with the new definition. Otherwise, the new definition will be pushed to the attributes array.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {...object} defs - Attribute definition object(s). See [`attributes`]{@link module:core/app.attributes} for valid definition properties.
 *
 * @see [`updateSettings`]{@link module:core/app.updateSettings}
 *
 * @example
 * // starting attributes definitions
 * [
 *   {
 *     type: 'filter',
 *     allowMultiple: false,
 *     primary: true,
 *   },
 * ]
 *
 * // define new primary attribute
 * let other = {
 *   type: 'other',
 *   allowMultiple: true,
 *   primary: true,
 * };
 *
 * // redefine filter so it isn't primary
 * let filter = {
 *   type: 'filter',
 *   allowMultiple: false,
 * };
 *
 * updateAttributeDefs(other, filter);
 *
 * // updated attribute definitions
 * [
 *   {
 *     type: 'other',
 *     allowMultiple: true,
 *     primary: true,
 *   },
 *   {
 *     type: 'filter',
 *     allowMultiple: false,
 *   },
 * ]
 */
export const updateAttributeDefs = (...defs) => {
  for (let def of defs) {
    // check if this attribute of this type is already defined
    let defIndex = Util.indexOfObjectByProperty('type', def.type, attributes);
    // if defined, replace with current def
    if (defIndex !== -1) attributes[defIndex] = def;
    // otherwise push new def
    else attributes.push(def);
    // update primary attribute in case it's changed
    updateSettings({ primary_attribute: getPrimaryAttribute()})
  }
};

/**
 * Get all attributes from either the path, active view inputs, or arbitrary inputs.
 *
 * Returns an object with properties `attributes` and `attr`, which are objects containing uncondensed or [condensed]{@link module:core/util.condenseAttributes} attributes for each [attribute type]{@link module:core/app.attributes}.
 *
 * @method
 *
 * @param {string} source - Name of the "source" of the attributes. Each valid source will gather attributes in a different way. Valid sources:
 *
 * - `'path'` - Gets attributes from path nodes using [`pathAttributes`]{@link module:core/app~pathAttributes}.
 * - `'active'` - Gets attributes from active view selections using [`View.activeAttributes`]{@link module:core/view.activeAttributes}. Note that this view method gets attributes that may be attached to the current view in addition to any active selections, which is why it should be used instead of simply passing active inputs under the "inputs" `source` param.
 * - `'inputs'` - Gets attributes from inputs provided as an array of elements using [`View.inputAttributes`]{@link module:core/view.inputAttributes}.
 *
 * @param {array|jQuery} [inputs] - Any valid [`View.inputAttributes(inputs)`]{@link module:core/view.inputAttributes} value.
 *
 * @param {boolean} [includeMeta] - When enabled, attribute meta for condensed `attr` will be included as `attrMeta`. Default: `false`
 *
 * @return {object}
 *
 * @see [`pathAttributes`]{@link module:core/app~pathAttributes}
 * @see [`View.activeAttributes`]{@link module:core/view.activeAttributes}
 * @see [`View.inputAttributes`]{@link module:core/view.inputAttributes}
 * @see Used by:
 * @see [**`setAttributes`**]{@link module:core/app.setAttributes}
 *
 * @example
 * // get attributes from path
 * getAttributesFrom('path');
 *
 * // example result, only showing filter
 * {
 *   // uncondensed attributes
 *   attributes: {
 *     filter: [
 *       {
 *         name: 'processor',
 *         value: 'i7',
 *         priority: 2,
 *       },
 *       {
 *         name: 'storage_amount',
 *         value: 500,
 *       },
 *       {
 *         name: 'processor',
 *         value: 'celeron',
 *         priority: 6,
 *       },
 *     ],
 *   },
 *   // condensed attributes
 *   attr: {
 *     filter: {
 *       processor: 'i7', // highest priority processor is kept
 *       storage_amount: 500,
 *     },
 *   },
 * }
 */
export const getAttributesFrom = (source, inputs, includeMeta = false) => {
  let data = {
    attributes: {}, // uncondensed attributes
    attr: {}, // condensed attributes
  };

  // create meta object, if necessary
  if (includeMeta) data.attrMeta = {};

  for (let attrDef of attributes) {
    let type = attrDef.type;

    // get uncondensed attributes
    if (source === 'path') data.attributes[type] = pathAttributes(attrDef);
    if (source === 'active') data.attributes[type] = View.activeAttributes(attrDef);
    if (source === 'inputs') data.attributes[type] = View.inputAttributes(inputs, attrDef);

    // get condensed attributes
    data.attr[type] = Util.condenseAttributes(data.attributes[type], attrDef);

    // get attribute meta, if requested
    if (includeMeta) data.attrMeta[type] = Util.condenseAttributes(data.attributes[type], attrDef, 'value', true);
  }

  return data;
};

/**
 * Gather and store active attributes for both current node and app path. [Both uncondensed & condensed attributes]{@link module:core/app.getAttributesFrom} are gathered for each.
 *
 * Attributes for the current node are retrieved from current active inputs, then [stored in the current node]{@link module:core/app.updatePathNode}. After node attributes are updated, path attributes are [gathered and stored in `state`]{@link module:core/app.updateState}.
 *
 * To access current or previous node attributes, use `state.current.[attributes|attr]` or `state.previous.[attributes|attr]`.
 *
 * To access app attributes, use `state.[attributes|attr]`.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array|jQuery} [inputs] - Array of inputs representing current node active inputs, if view active inputs are not available. See `inputs` param definition in [`View.inputAttributes`]{@link module:core/view.inputAttributes} for valid values.
 * @param {boolean} merge - When enabled, path node attributes will be merged instead of assigned. Default: `false`
 *
 * @see [`getAttributesFrom`]{@link module:core/app.getAttributesFrom}
 * @see [`updatePathNode`]{@link module:core/app.updatePathNode}
 * @see [`updateState`]{@link module:core/app.updateState}
 */
export const setAttributes = (inputs = [], merge = false) => {
  // get attributes
  const nodeAttr = inputs.length ? getAttributesFrom('inputs', inputs) : getAttributesFrom('active');

  // update node
  updatePathNode(nodeAttr, merge);

  debug(2, 'Node state updated', state.current);

  // update app attributes
  updateState(getAttributesFrom('path'));

  debug(1, '%cApp state', 'font-weight:bold; color:white;', Object.assign({}, state));
};

/**
 * Modify view content as necessary for app functionality. See `args` param for more information on the transformations applied.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} c - The view content to transform.
 *
 * @param {string} [k] - Current content key. If the current iteration object is an array, this value will be an array index, otherwise it will be a property name. If no initial value is provided, the first key in the `id` will be the child object's. Default: `''`
 *
 * @param {array} [l] - The location key accumulator. It is not necessary to provide a value for this argument, as it is used to accumulate a location history for each branch of the object traversed. Default: `[]`
 *
 * @param {object|boolean} [args] - Object containing information about which transforms should be run. All transforms are enabled by default.
 *
 * @param {boolean} [args.id] - When enabled, each object in `view.content` gets assigned an `id` string based on its location in the content object (e.g. `content-0-object`, which is then [encoded]{@link module:core/util.encodeID} to reduce size). This string is unique for each object, and may be safely used in the view as an HTML `id` attribute. Note that any objects that are children to any of the following properties do not receive IDs: `settings`, `attributes`, `conditional_on`.
 *
 * @param {boolean} [args.blob] - When enabled, if a value is a Blob, it is replaced with an object URL for the given Blob.
 *
 * @param {boolean} [args.string] - When enabled, any string references will be replaced with the relevant content, where possible.
 *
 * #### Reference tags
 * If a string value contains a reference to a label (e.g. `"{{ site_title }}"`), it will be replaced with the referenced value. If the referenced value is an array, the entire label string will be replaced with the referenced array, otherwise only the reference tag is replaced.
 *
 * #### Dynamic reference tags
 * If a string value contains a dynamic reference (e.g. `"[number] products remaining"`), and the referenced tag exists in the `dynamic_refs` settings object, the reference tag will be replaced with the string value for the given tag. *Note that replacing dynamic strings is not supported for referenced array content, i.e. if the target content of a *reference tag* is an array.*
 *
 * ##### Localize tag
 * Numeric values may be localized based on the current app locale by using the `localize` dynamic reference tag:
 *
 * ```
 * "price": "[localize:1199.99]"
 *
 * // result for local de_DE
 * price: "1.199,00"
 * ```
 *
 * @param {boolean} [args.object] - When enabled, object transformations will occur. These include:
 *
 * - Objects containing global `conditional_on` conditions will be checked and, if unavailable due to previous selections, they will be stripped. This prevents content that cannot be used from reaching the view. Additional object transforms may also be
 * - Repeater field type content will be generated, if applicable. The provided repeater block content is duplicated up to the specified max blocks. *Note that only single-level repeater content is supported at this time (no nested repeater fields).*
 *
 * @see [`conditionalOn`]{@link module:core/app.conditionalOn}
 * @see Used by:
 * @see [**`setCurrentContent`**]{@link module:core/app~setCurrentContent}
 */
export const transformContent = (c, k = '', l = [], {id = true, blob = true, string = true, object = true} = {}) => {
  // define properties to exclude transforming
  let excludeIDs = ['electron'];

  // do nothing of content is not an object
  if (typeof c !== 'object' || excludeIDs.includes(k)) return;

  // copy location
  l = l.slice();

  // determine if current object is an array
  let isArray = Array.isArray(c);

  // define transform functions
  // IDs
  const IDTransform = (c, l) => {
    // if is array, or not transforming IDs, do nothing
    if (isArray || !id) return;
    // define properties to exclude adding IDs to
    let excludeIDs = ['settings', 'attributes', 'conditional_on'];
    // add ID if no location property matches an excluded property
    if (excludeIDs.reduce((add, property) => l.includes(property) ? false : add, true)) c.id = Util.encodeID(l.join('-'));
  };

  // Blob
  const blobTransform = (c, k) => {
    // if not a blob, or not transforming blobs, do nothing
    if (!(c[k] instanceof Blob) || !blob) return;
    // create blob object url
    c[k] = URL.createObjectURL(c[k]);
  };

  // string
  const stringTransform = (c, k) => {
    // if not a string, or not transforming strings, do nothing
    if (typeof c[k] !== 'string' || !string) return;

    // if direct_embed is enabled, replace relative assets location with syndicated assets location
    if (config.direct_embed.enabled && !c[k].includes('//') && c[k].includes('assets/')) c[k] = config.syndication_current + c[k];

    // find label references
    let refs = c[k].match(/{{(.*?)}}/g);
    // if no label references exist, do nothing
    if (refs) {
      // if labels aren't defined yet in settings, use content labels
      let labelsObj = (settings.labels && Object.keys(settings.labels).length) ? settings.labels : content.settings.labels;
      // get referenced content
      let refContent = refs.map(ref => Util.getObjectValue(labelsObj, ref.replace(/{{|}}/g, '').trim(), '.'));
      // replace label content
      // if only one reference, and referenced content is an array, replace entire key
      if (refContent.length === 1 && Array.isArray(refContent[0])) c[k] = refContent[0];
      // otherwise just replace the reference tag
      else for (let i = 0; i < refs.length; i++) c[k] = c[k].replace(refs[i], refContent[i]);
    }
    // if label reference is no longer a string, do nothing (for now; array support may come in the future)
    if (typeof c[k] !== 'string') return;
    // find dynamic references
    let dynamicRefs = c[k].match(/\[(.*?)]/g);
    // if no dynamic refs, stop here
    if (!dynamicRefs) return;
    // replace any dynamic refs with necessary content
    for (let ref of dynamicRefs) {
      // if settings are not yet set up, use config refs
      let dynamicRefContents = settings.dynamic_refs || config.dynamic_refs;
      // get tag content without brackets
      let tag = ref.match(/\[(.*)]/)[1];
      // initialize reference content
      let refContent = '';
      // if using localize tag, get localized value
      if (tag.includes('localize')) {
        let value = tag.split(':')[1];
        refContent = Util.formatNumber(parseFloat(value));
      }
      // check for defined dynamic refs
      else {
        // get the value of the ref content property
        let refValue = dynamicRefContents[tag];
        // if no value, skip
        if (!refValue) continue;
        // set ref content to either the primitive value, or the returned value of a function
        refContent = typeof refValue === 'function' ? refValue() : refValue;
      }
      // set new string value
      c[k] = c[k].replace(ref, refContent);
    }
  };

  // object
  const objectTransform = (c, k, keys, i) => {
    // if not an object, or not transforming objects, do nothing
    if (typeof c[k] !== 'object' || !object) return [keys, i];
    // create object for iteration state
    let s = {keys, i};
    // remove unavailable global conditional items, and stop processing
    if (!conditionalTransform(c, k, s)) return [s.keys, s.i];
    // process repeater block content
    repeaterTransform(c[k]);
    // return keys and i
    return [keys, i];
  };

  // remove conditional items
  const conditionalTransform = (c, k, s) => {
    // get object conditions, if they exist
    let conditions = c[k].conditional_on;
    // if no conditions, or the conditional check passes, keep content
    if (!(conditions && !conditionalOn(conditions, true))) return true;
    // if conditional check fails, delete item and decrement keys
    debug(3, 'Global conditional item removed', c[k]);
    isArray ? c.splice(k, 1) : delete c[k];
    // update keys list
    s.keys = Object.keys(c);
    // decrement i to keep the correct loop position
    s.i--;
    // return false to indicate removed content
    return false;
  };

  // process repeater block content
  const repeaterTransform = (c) => {
    let fieldType = c.field_type;
    if (!(fieldType && fieldType === 'repeater')) return;
    let addAttrBlock = (o, i) => {
      if (typeof o !== 'object') return;
      if (o.types && o.name) o.block = i;
      else for (let k of Object.keys(o)) addAttrBlock(o[k], i);
    };
    // default to 10 blocks max
    let max = c.max_blocks || settings.view.form.repeater.max_blocks;
    // create blocks array
    c.blocks = [];
    // duplicate block until max is reached
    for (let i = 0; i < max; i++) {
      c.blocks[i] = Util.cloneDeep(c.block);
      // add block number to attributes
      addAttrBlock(c.blocks[i], i);
    }
  };

  // add key to location, if one exists
  if (k) l.push(k);
  // loop through each key to recurse deeper
  let keys = Object.keys(c);
  // loop through each key to apply transform processes
  for (let i = 0; i < keys.length; i++) {
    let k = keys[i];
    // if content is undefined, show a notice
    if (typeof c[k] === 'undefined') debug(3, `Notice: undefined content at: ${l.join('.')}.${k}`);
    blobTransform(c, k);
    stringTransform(c, k);
    [keys, i] = objectTransform(c, k, keys, i);
    transformContent(c[k], k, l, {id, blob, string, object});
  }

  // add object IDs
  IDTransform(c, l, isArray);
};

/**
 * Transform object label references into actual referenced label item. Content is modified in place, and the modified object returned.
 *
 * Note that this method simply runs [`transformContent`]{@link module:core/app.transformContent} with only the `args.string` param enabled.
 *
 * @method
 *
 * @since 3.3.0
 *
 * @param {object} content - Content object to transform labels.
 *
 * @return {object}
 *
 * @see [`transformContent`]{@link module:core/app.transformContent}
 * @see Used by:
 * @see [**`settingsInit`**]{@link module:core/app~settingsInit}
 */
export const replaceLabels = (content) => {
  // transform content
  transformContent(content, '', [], { id: false, blob: false, object: false });
  // return transformed content
  return content;
};

/**
 * Set current view deep link URL when `dev_mode` [setting]{@link module:core/config.app_settings} is enabled, as long as this the app is not in [view all]{@link module:core/app.isViewAll} mode.
 *
 * Deep linking allows for "dev reload" during [`start`]{@link module:core/app.start}.
 *
 * Parameters added:
 * - `location` - current "location" or view ID
 * - `dq` - destination queue
 * - All attributes from [primary attribute]{@link module:core/app.attributes}
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`isViewAll`]{@link module:core/app.isViewAll}
 * @see Used by:
 * @see [**`View.postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 * @see [**`View.closeContentModal`**]{@link module:core/view~closeContentModal}
 *
 * @example
 * // example query string
 * // user has made selections specifying the "notebook" form factor
 * // plus several pages to queue, which will be visited in order
 * ?location=page_2&form_factor=notebook&dq=page_3,page4
 */
export const updateHistory = () => {
  // don't run if not dev mode
  if (!settings.dev_mode) return;
  // create param object
  let paramObj = {
    location: state.current.location,
    //p: Util.encode(state.current.index, state.current.location),
  };
  // if not view all, add attributes and destination queue
  if (!isViewAll()) {
    // add primary attributes
    Util.merge(paramObj, state.attr[settings.primary_attribute.type]);
    // add destination queue
    if (state.previous.dq.length) paramObj.dq = state.previous.dq;
  }
  // create query string
  const params = Util.queryString(paramObj);
  // set dev url
  window.history.pushState({}, "", `${window.location.pathname}?${params}`);
};

/**
 * Clear deep link query string. This is done during [`start`]{@link module:core/app.start} in non-dev-mode reloads, to prevent any leftover dev mode query strings from affecting the app.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see Used by:
 * @see [**`start`**]{@link module:core/app.start}
 * @see [**`updateHistory`**]{@link module:core/app.updateHistory}
 */
export const clearDeepLink = () => {
  window.history.pushState({}, "", window.location.pathname);
};

/**
 * Generate share recommendation link, which consists of the base `share_url` [app setting]{@link module:core/config.app_settings}, and a query parameter representing any products shown in the current page.
 *
 * The share recommendation URL is checked for existing querystring params, then the `srt_share` and encoded param data are added to the query. Encoded data consists of a [Base64 encoded]{@link module:core/util.encode} list of product IDs.
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {array} products - Products to use in recommendation share link. If none are available, the share link will only consist of the base `share_url` value. Default: `current.products`
 *
 * @see Used by:
 * @see [**`setCurrentContent`**]{@link module:core/app~setCurrentContent}
 *
 * @example
 * // example share link
 * 'https://example.com/path/to/app/?srt_share=MTAxNDQxODcxLDEwMTA2Mzc4MywxMDE0OTAyNTksMTAxMDc1NjA3LDEwMTM2OTY0MSwxMDEwMjI2MDU'
 */
export const getShareRecommendationLink = (products = current.products) => {
  // get share url destination
  let url = settings.share_url;
  // get url base and query elements
  let [urlBase] = url.split('?');
  let query = Util.getURLParam('', url) || {};
  // if products are present, create query param for products
  if (products && products.length) query.srt_share = Util.encode(products.map(product => product.product_id).join(','));
  // return recommendation link
  let queryString = Util.queryString(query);
  if (queryString) queryString = `?${queryString}`;
  return `${urlBase}${queryString}`;
};

/**
 * Check if this is a "view all inventory" request. This value will be true if either `window.SRT_VIEW_ALL` or `settings.srt_view_all` is `true`.
 *
 * The `srt_view_all` setting is enabled when [`viewAllInit`]{@link module:core/app.viewAllInit} is run.
 *
 * To trigger view all mode with an HTML template, the `window` object must have the property `SRT_VIEW_ALL` set to `true`.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @return {boolean}
 *
 * @see Used by:
 * @see [**`updateHistory`**]{@link module:core/app.updateHistory}
 * @see [**`Filter.finalizeResults`**]{@link module:core/filter~finalizeResults}
 *
 * @example
 * <!-- set "view all" trigger in an html template: -->
 * <script>window.SRT_VIEW_ALL = true;</script>
 */
export const isViewAll = () => {
  return !!window.SRT_VIEW_ALL || settings.srt_view_all;
};

/**
 * Set up "view all" content.
 *
 * - "View all" content is defined, [customized]{@link module:core/app.customize} (if necessary), and [appended]{@link module:core/app.appendContent} to [`content`]{@link module:core/app~content} under the view ID `all_inventory`.
 * - Template `view-all` is added to `settings.templates`. This is name of the template that will display view all content.
 * - [Related settings are enabled]{@link module:core/app.toggleViewAllSettings}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @example
 * // default view all content
 * {
 *   all_inventory: {
 *     template: 'view-all',
 *     title: 'View inventory',
 *     settings: {
 *       get_products: -1,
 *     },
 *     ref: {
 *       content: settings.end,
 *     },
 *   },
 * }
 *
 * @see [`customize`]{@link module:core/app.customize} `viewAllContent`
 * @see [`toggleViewAllSettings`]{@link module:core/app.toggleViewAllSettings}
 * @see [`appendContent`]{@link module:core/app.appendContent}
 */
export const viewAllInit = () => {
  // add view all template to templates list
  Util.arrayPushUnique(settings.templates, 'view-all');
  // setup default view all content
  let viewAllContent = {
    template: 'view-all',
    title: 'View inventory',
    settings: {
      get_products: -1,
    },
    ref: {
      content: settings.end,
    },
  };
  // allow for project modifications
  customize('viewAllInit', viewAllContent);
  // define view ID for view all
  let viewID = 'all_inventory';
  // build config details
  let configs = appConfigs || {};
  configs.paths.push('final config');
  configs.data.push({settings: Util.cloneDeep(appData.settings)});
  let config_details = [];
  for (let i = 0; i < configs.paths.length; i++) {
    let path = configs.paths[i];
    let data = configs.data[i].settings;
    delete data.buildInfo;
    config_details.push({path, data});
  }
  // update settings
  toggleViewAllSettings(true, {config_details});
  // add view all content to main content object
  appendContent(viewID, viewAllContent);
  // return the "view all" view id
  return viewID;
};

/**
 * Toggle on or off settings related to "view all" mode, including:
 *
 * - `srt_view_all` - set to `enable` value
 * - `view.show_more_products.enabled` - set to `enable` value
 * - `reload_products_on_state_change` - set to `enable` value
 *
 * @method
 *
 * @param {boolean} enable - When enabled, "view all" settings will be turned on, otherwise they are turned off. Default: `true`
 *
 * @param {object} data - Additional data to pass into updated settings object.
 */
export const toggleViewAllSettings = (enable = true, data = {}) => {
  updateSettings({
    srt_view_all: enable,
    view: {
      show_more_products: {
        enabled: enable,
      },
      reload_products_on_state_change: enable,
    },
  }, data);
};

/**
 * Modify app data or run project-specific methods for selected core methods.
 *
 * Checks for the existence of [project customization]{@link module:project/customize} methods with the provided `hook` name. If the method exists, supplied data are passed as parameters, and method's result is returned (if any). The naming convention for `hook` is to mirror the name of the method that called `customize` (without module prefix), so project methods should be named the same as the methods that call them.
 *
 * To enable customization on a method, add `customize('methodName')` inside the method to be extended, wherever the "extension" is needed. If any method data are needed to facilitate customization, it can be passed in as additional parameters.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} hook - Method name from [`project/customize`]{@link module:project/customize} to run. All valid `hook` values and their methods are listed in the "see more" section.
 *
 * @param {...*} data - Data to pass to project method. Typically includes the original method's parameters, the value to be returned, and/or data that may only be available inside the parent method. Check individual method documentation see what `data` parameters are passed.
 *
 * @return {*}
 *
 * @see [`App.init`]{@link module:core/app.init}
 * @see [`App.configInit`]{@link module:core/app~configInit}
 * @see [`App.settingsInit`]{@link module:core/app~settingsInit}
 * @see [`App.attributesInit`]{@link module:core/app~attributesInit}
 * @see [`App.viewAllInit`]{@link module:core/app.viewAllInit}
 * @see [`App.setCurrentContent`]{@link module:core/app~setCurrentContent}
 * @see [`App.setProductContent`]{@link module:core/app~setProductContent}
 * @see [`App.getProducts`]{@link module:core/app~getProducts}
 * @see [`View.navigate`]{@link module:core/view.navigate}
 * @see [`View.navigateOffsite`]{@link module:core/view.navigateOffsite}
 * @see [`View.inputActions`]{@link module:core/view.inputActions}
 * @see [`View.handleConditionals`]{@link module:core/view.handleConditionals}
 * @see [`View.closeModals`]{@link module:core/view.closeModals}
 * @see [`View.addEventListeners`]{@link module:core/view~addEventListeners}
 * @see [`View.preRenderViewActions`]{@link module:core/view~preRenderViewActions}
 * @see [`View.postRenderViewActions`]{@link module:core/view~postRenderViewActions}
 * @see [`View.exitView`]{@link module:core/view~exitView}
 * @see [`View.renderReady`]{@link module:core/view~renderReady}
 * @see [`View.removeUnusedElements`]{@link module:core/view~removeUnusedElements}
 * @see [`View.setActiveSelections`]{@link module:core/view~setActiveSelections}
 * @see [`View.resize`]{@link module:core/view~resize}
 * @see [`Filter.lintProducts`]{@link module:core/filter~lintProducts}
 * @see [`Filter.setProductAttributes`]{@link module:core/filter~setProductAttributes}
 * @see [`Filter.setProductLabels`]{@link module:core/filter~setProductLabels}
 * @see [`Filter.setProductSpecs`]{@link module:core/filter~setProductSpecs}
 *
 * @example
 * // extend SRT [project/customize.js]
 * // using init() as an example, but any function listed above is supported
 * export const init = () => {
 *   projectMethod();
 * };
 *
 * // inner project method
 * const projectMethod = () => {
 *   // do project things
 * };
 *
 * @example
 * // add project default config [project/customize.js]
 * const config = {
 *   project_setting: true,
 * };
 *
 * export const configInit = () => {
 *   App.updateConfig(config);
 * };
 */
export const customize = (hook, ...data) => {
  if (typeof Customize[hook] === 'function') return Customize[hook](...data);
};

/**
 * Helper function to flag build issues, which are reported on the [view all]{@link module:core/app.viewAllInit} screen.
 *
 * @method
 *
 * @since 3.3.2
 *
 * @see [`updateSettings`]{@link module:core/app.updateSettings}
 * @see Used by:
 * @see [**`Filter.addProductBuildInfo`**]{@link module:core/filter~addProductBuildInfo}
 * @see [**`View.addImageBuildInfo`**]{@link module:core/view~addImageBuildInfo}
 *
 * @example
 * // property added to settings
 * {
 *   build_issue: true,
 * }
 */
export const flagBuildIssue = () => {
  updateSettings({build_issue: true});
};

/**
 * Debug
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {boolean|number} level - Debug level at which the provided `content` should be shown.
 * @param {*} content - Content to log.
 */
export const debug = (level, ...content) => {
  if (settings.debug === true || settings.debug >= level) {
    console.log(...content);
  }
};

/**
 * Methods for interacting with the app's IndexedDB database.
 *
 * All IndexedDB interactions are ultimately completed using the [`connect`]{@link module:core/app.db.connect} method, however operation-specific helper methods are included to simplify the arguments needed to complete an operation.
 *
 * @namespace
 *
 * @since 3.2.4
 *
 * @type {object}
 */
export const db = {
  /**
   * The current database version number, used in IndexedDB database connections.
   *
   * @memberOf module:core/app.db
   *
   * @since 3.2.4
   *
   * @see [`db.connect`]{@link module:core/app.db.connect}
   *
   * @example
   * version: 1
   */
  version: 1,

  /**
   * Add or update an object.
   *
   * Returns a promise, which resolves on successful put. Resolve returns a string containing the keyPath of the stored object.
   *
   * @memberOf module:core/app.db
   *
   * @since 3.2.4
   *
   * @param {object} data - The object to put.
   * @param {string} key - The key of the object to add/update. Default: `'config'`
   * @param {string} [store] - Name of the object store. Default: `'app'`
   *
   * @return {Promise<string>}
   *
   * @see [`db.connect`]{@link module:core/app.db.connect}
   * @see Used by:
   * @see [**`updatePersistentSettings`**]{@link module:core/app~updatePersistentSettings}
   * @see [**`Filter.updateProduct`**]{@link module:core/filter.updateProduct}
   *
   * @example
   * // object to store
   * let product = {
   *   name: 'Acer Inspire 1234',
   *   product_id: 'TYRHS-83702',
   * }
   *
   * db.put(product, product.product_id, 'products')
   *   .then(result => { console.log(result) }); // 'TYRHS-83702'
   */
  put: (data, key = 'config', store = 'app') => db.connect('put', {key, data, store}),

  /**
   * Add / update array of objects.
   *
   * Returns a promise, which resolves on successful transaction. Resolve returns a boolean `true`.
   *
   * @memberOf module:core/app.db
   *
   * @since 3.2.4
   *
   * @param {array} data - Array of objects to put.
   * @param {string} [store] - Name of the object store. Default: `'app'`
   *
   * @return {Promise<boolean>}
   *
   * @see [`db.connect`]{@link module:core/app.db.connect}
   * @see Used by:
   * @see [**`Filter.init`**]{@link module:core/filter.init}
   *
   * @example
   * // objects to store
   * let products = [
   *   {
   *     name: 'Acer Inspire 1234',
   *     product_id: 'TYRHS-83702',
   *   },
   *   {
   *     name: 'Dell Inspiron 4321',
   *     product_id: '8D90HK.S382',
   *   },
   * ];
   *
   * db.putAll(products, 'products')
   *   .then(result => { console.log(result) }); // true
   */
  putAll: (data, store = 'app') => db.connect('putAll', {data, store}),

  /**
   * Get an object using its key.
   *
   * Returns a promise, which resolves on successful transaction. Resolve returns the retrieved object.
   *
   * @memberOf module:core/app.db
   *
   * @since 3.2.4
   *
   * @param {string} key - Key of the object to retrieve. Default: `'config'`
   * @param {string} [store] - Name of the object store. Default: `'app'`
   *
   * @return {Promise<object>}
   *
   * @see [`db.connect`]{@link module:core/app.db.connect}
   * @see Used by:
   * @see [**`init`**]{@link module:core/app.init}
   *
   * @example
   * // product id to retrieve
   * let id = 'TYRHS-83702'
   *
   * db.get(id, 'products')
   *   .then(result => { console.log(result) });
   *
   * // expected result
   * {
   *   name: 'Acer Inspire 1234',
   *   product_id: 'TYRHS-83702',
   * }
   */
  get: (key = 'config', store = 'app') => db.connect('get', {key, store}),

  /**
   * Get all objects in an object store.
   *
   * Returns a promise, which resolves on successful transaction. Resolve returns array of retrieved objects.
   *
   * @memberOf module:core/app.db
   *
   * @since 3.2.4
   *
   * @param {string} [store] - Name of the object store. Default: `'app'`
   *
   * @return {Promise<array>}
   *
   * @see [`db.connect`]{@link module:core/app.db.connect}
   * @see Used by:
   * @see [**`Filter.init`**]{@link module:core/filter.init}
   *
   * @example
   * // retrieve all products
   * db.getAll('products')
   *   .then(result => { console.log(result) });
   *
   * // expected result
   * [
   *   {
   *     name: 'Acer Inspire 1234',
   *     product_id: 'TYRHS-83702',
   *   },
   *   {
   *     name: 'Dell Inspiron 4321',
   *     product_id: '8D90HK.S382',
   *   },
   * ]
   */
  getAll: (store = 'app') => db.connect('getAll', {store}),

  /**
   * Delete an object.
   *
   * Returns a promise, which resolves on successful transaction. Resolve returns a boolean `true`.
   *
   * @memberOf module:core/app.db
   *
   * @since 3.2.4
   *
   * @param {string} key - Key of the object to delete.
   * @param {string} [store] - Name of the object store. Default: `'app'`
   *
   * @return {Promise<boolean>}
   *
   * @see [`db.connect`]{@link module:core/app.db.connect}
   * @see Used by:
   * @see [**`Filter.updateProduct`**]{@link module:core/filter.updateProduct}
   * @see [**`Filter.deleteProduct`**]{@link module:core/filter.deleteProduct}
   *
   * @example
   * // product to remove
   * let id = '8D90HK.S382';
   *
   * db.remove(id, 'products')
   *   .then(result => { console.log(result) }); // true
   */
  remove: (key, store = 'app') => db.connect('remove', {key, store}),

  /**
   * Remove all objects from an object store.
   *
   * Returns a promise, which resolves on successful transaction. Resolve returns a boolean `true`.
   *
   * @memberOf module:core/app.db
   *
   * @since 3.2.4
   *
   * @param {string} [store] - Name of the object store. Default: `'app'`
   *
   * @return {Promise<boolean>}
   *
   * @see [`db.connect`]{@link module:core/app.db.connect}
   *
   * @example
   * db.clear('products')
   *   .then(result => { console.log(result) }); // true
   */
  clear: (store = 'app') => db.connect('clear', {store}),

  /**
   * Establish connections to the app's IndexedDB database, named `srt`, and run desired operations on it. Uses `db.version` as the version number.
   *
   * Returns a promise, which resolves on successful transaction. Resolve return value will vary depending on the operation.
   *
   * Note that while any operation can be run by using this method directly in the app, the helper methods named after the operations simplify usage and are the recommended way to interact with the database. As such, documentation for each operation is located with its respective helper method.
   *
   * ### IndexedDB event listener callbacks
   *
   * #### `onerror`
   * Reject promise, and return the error.
   *
   * #### `onupgradeneeded`
   * Run actions depending on the current database version number. Actions run for each version
   * - If db version < 0 (initial load), create `app` and `products` object store.
   *
   * #### `onsuccess`
   * - Define current transaction mode and initialize transaction.
   * - Run desired operation method:
   *     - `put` - Use [IDBObjectStore method `put`]{@link https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/put} to store object. If `options.store` is `'app'`, the supplied keyPath will be added to the object before storing.
   *     - `putAll` - Loop through data array and use [IDBObjectStore method `put`]{@link https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/put} to store each object. If `options.store` is `'app'`, the supplied keyPath will be added to each object before storing.
   *     - `get` - Use [IDBObjectStore method `get`]{@link https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/get} to retrieve an object at the requested keyPath.
   *     - `getAll` - Use [IDBObjectStore method `getAll`]{@link https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/getAll} to retrieve all objects in a given object store.
   *     - `remove` - Use [IDBObjectStore method `delete`]{@link https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/delete} to remove an object at the requested keyPath.
   *     - `clear` - Use [IDBObjectStore method `clear`]{@link https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/clear} to remove all objects in a given object store.
   *
   *
   * @memberOf module:core/app.db
   *
   * @since 3.2.4
   *
   * @param {string} method - Name of the desired operation. Valid methods include: [`put`]{@link module:core/app.db.put} [`putAll`]{@link module:core/app.db.putAll} [`get`]{@link module:core/app.db.get} [`getAll`]{@link module:core/app.db.getAll} [`remove`]{@link module:core/app.db.remove} [`clear`]{@link module:core/app.db.clear}
   *
   * @param {object} options - Parameters necessary to complete desired operation.
   *
   * @param {string} options.store - Name of the store to operate on.
   *
   * @param {string} [options.key] - Object keyPath. Required by `put`, `get`, and `remove` methods.
   *
   * @param {object|array} [options.data] - Data to store. Required by `put` and `putAll` methods.
   *
   * @return {Promise}
   *
   * @see [`db.version`]{@link module:core/app.db.version}
   * @see Used by:
   * @see [**`db.put`**]{@link module:core/app.db.put}
   * @see [**`db.putAll`**]{@link module:core/app.db.putAll}
   * @see [**`db.get`**]{@link module:core/app.db.get}
   * @see [**`db.getAll`**]{@link module:core/app.db.getAll}
   * @see [**`db.remove`**]{@link module:core/app.db.remove}
   * @see [**`db.clear`**]{@link module:core/app.db.clear}
   */
  connect: (method, options) => {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) reject('IndexedDB is not available.');

      // if cookies are disabled, reject
      if (!navigator.cookieEnabled) {
        resolve();
        return;
      }

      // get request
      let request = window.indexedDB.open('srt', db.version);

      // error
      request.onerror = () => { reject(request.error) };

      // on blocked
      request.onblocked = () => { reject('IndexedDB blocked.') };

      // on upgrade
      request.onupgradeneeded = (event) => {
        let db = event.target.result;
        // create database
        if (event.oldVersion < 1) {
          db.createObjectStore('app', {keyPath: 'object_name'});
          db.createObjectStore('products', {keyPath: 'product_id'});
        }
      }

      // on success
      request.onsuccess = (event) => {
        // define database object
        let db = event.target.result;
        // define transaction mode
        let mode = (['get', 'getAll'].includes(method)) ? 'readonly' : 'readwrite';
        // initialize transaction
        let transaction = db.transaction([options.store], mode);
        // define success action
        let success = (event) => { resolve(event.target.result || true) };
        // on failure
        transaction.onerror = () => { reject(transaction.error) };
        // define operation actions
        let operations = {
          // put data
          put: () => {
            // if store is "app" add keyPath to data object
            if (options.store === 'app') options.data.object_name = options.key;
            transaction.objectStore(options.store).put(options.data).onsuccess = success;
          },
          // put all data
          putAll: () => {
            let objectStore = transaction.objectStore(options.store);
            for (let item of options.data) {
              if (options.store === 'app') item.object_name = options.key;
              objectStore.put(item);
            }
            transaction.oncomplete = success;
          },
          // get data
          get: () => {
            transaction.objectStore(options.store).get(options.key).onsuccess = success;
          },
          // get all data
          getAll: () => {
            transaction.objectStore(options.store).getAll().onsuccess = success;
          },
          // remove data
          remove: () => {
            transaction.objectStore(options.store).delete(options.key).onsuccess = success;
          },
          // clear object store
          clear: () => {
            transaction.objectStore(options.store).clear().onsuccess = success;
          },
        };

        // if no operation recognized, reject
        if (!operations[method]) {
          reject('No recognized App.db.connect operation specified.');
          return;
        }

        // run operation
        operations[method]();
      };
    });
  },
};

/*
INTERNAL MODULE FUNCTIONS
 */

/**
 * Initialize [`config`]{@link module:core/app~config} object with additional configuration defaults. Changes to config are made using [`updateConfig`]{@link module:core/app.updateConfig}.
 *
 * - [Retrieve stored settings from database]{@link module:core/app.db.get}. When promise resolves:
 * - Add any entry-supplied configs.
 * - Run [customize]{@link module:core/app.customize} hook to add project config defaults.
 * - Add stored config settings, which were received from promise resolve.
 * - If a `lang` is included in stored settings, and stored `lang` is available in build `langs`, add it to config.
 *
 * Returns promise once complete.
 *
 * @since 3.2.0
 *
 * @param {object} entryConfig - Configuration settings from entry.
 *
 * @return {Promise}
 *
 * @see [`db.get`]{@link module:core/app.db.get}
 * @see [`updateConfig`]{@link module:core/app.updateConfig}
 * @see [`customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`init`**]{@link module:core/app.init}
 */
const configInit = (entryConfig) => {
  // get stored settings
  return db.get()
    .finally(() => {
      // add provided configs
      updateConfig(entryConfig);
      // add appData settings
      updateConfig(appData.settings);
      // if js embed is enabled, update assets locations with syndication URL
      if (config.direct_embed.enabled) {
        updateConfig({
          lang_content_location: `${config.syndication_current}content`,
          lang_styles_location: `${config.syndication_current}assets/styles`,
        });
      }
      // add project config
      customize('configInit');
    })
    .then(stored => {
      // if persistent settings is not enabled, or stored content is not valid, do nothing
      if (!config.persistent || !config.persistent.enabled || !stored || typeof stored !== 'object') return;
      // if a lang is included in stored settings, and stored lang is available in build langs, add it to config
      // this is needed because the lang is used right after config init during content init
      if (stored.lang && config.langs.includes(stored.lang)) updateConfig({lang: stored.lang});
    });
};

/**
 * Set up app [`settings`]{@link module:core/app.settings}. Combines default settings, build and config settings, and content settings into a final settings object, and injects any additional dynamic settings that may be necessary.
 *
 * Settings object is reset to default [`config`]{@link module:core/app~config} settings to start, and additional settings objects are merged in, in this order:
 * 1. Content JSON [`settings`]{@link module:core/config.app_settings}, after any [label references are replaced]{@link module:core/app.replaceLabels}.
 * 1. Dynamically-discovered settings, including:
 *     - `primary_attribute` - [Determine the app's primary attribute]{@link module:core/app~getPrimaryAttribute}.
 *     - `lang_dir` - [Get the current `lang` setting's writing direction]{@link module:core/app~getLangDir}.
 *     - `start` - If [in view all mode]{@link module:core/app.isViewAll}, initialize [view all content]{@link module:core/app.viewAllInit}, and set starting view ID to the view all ID.
 * 1. [Custom project settings]{@link module:core/app.customize}.
 * 1. Verify that current content has a valid `start` and `end` value, i.e. there is view content for each view ID.
 *
 * Note that app settings provided through content JSON `settings` will override any build-specific settings. This can be used to influence app behavior post-build, like enabling [`iframe`]{@link module:core/config.iframe} functionality; however, if the setting requires functionality to be built in, it may not have an effect.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @returns {Promise}
 *
 * @see [`db.get`]{@link module:core/app.db.get}
 * @see [`updateSettings`]{@link module:core/app.updateSettings}
 * @see [`replaceLabels`]{@link module:core/app.replaceLabels}
 * @see [`getPrimaryAttribute`]{@link module:core/app~getPrimaryAttribute}
 * @see [`getLangDir`]{@link module:core/app~getLangDir}
 * @see [`isViewAll`]{@link module:core/app.isViewAll}
 * @see [`viewAllInit`]{@link module:core/app.viewAllInit}
 * @see [`customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`contentInit`**]{@link module:core/app.contentInit}
 *
 * @example
 * // example default settings
 * // note that build/config settings are already merged into config defaults during app init
 * {
 *   dev_mode: false,
 *   platform: "iframe",
 *   fallback_img_src: {
 *     notebook: "assets/images/fallback-product-notebook.svg",
 *     desktop: "assets/images/fallback-product-desktop.svg",
 *   },
 *   iframe: {
 *     enabled: true,
 *     offset: 0
 *   },
 * }
 *
 * // example content settings object
 * let settings = {
 *   fallback_img_src: {
 *     notebook: "assets/images/alternate-notebook.svg",
 *   },
 *   iframe: {
 *     offset: 90
 *   },
 * }
 *
 * // merge settings
 * // and there are additional dynamic settings added
 * updateSettings(settings);
 *
 * // final settings object
 * {
 *   dev_mode: true,
 *   platform: "iframe",
 *   fallback_img_src: {
 *     notebook: "assets/images/alternate-notebook.svg",
 *     desktop: "assets/images/fallback-product-desktop.svg",
 *   },
 *   iframe: {
 *     enabled: true,
 *     offset: 90
 *   },
 * }
 */
const settingsInit = () => {
  // get stored settings before making any updates
  return db.get()
    .finally(() => {
      // reset settings
      settings = {};
      // add config defaults, then add content settings, after replacing labels
      updateSettings(Util.cloneDeep(config), replaceLabels(content.settings));
      // define dynamic settings
      let dynamicSettings = {
        primary_attribute: getPrimaryAttribute(),
        lang_dir: getLangDir(),
      };
      // merge in dynamic settings
      updateSettings(dynamicSettings);
      // if "view all" has been enabled externally, initialize view all
      if (isViewAll()) viewAllInit();
      // add project settings
      customize('settingsInit');
    })
    .then(stored => {
      // if no persistent settings, or stored content is not valid, do nothing
      if (!settings.persistent.enabled || !stored || typeof stored !== 'object') return;
      // re-add stored settings (except lang)
      if (stored.lang) delete stored.lang;
      updateSettings(stored);
    })
    .finally(() => {
      // check that our start/end are valid locations
      if (!content[settings.start] || !content[settings.end]) throw 'Settings "start" or "end" view IDs do not exist.';
    });
};

/**
 * Create a new [`path`]{@link module:core/app~path} node object.
 *
 * This function takes an object argument that can be used to override or pre-populate the node with certain data. However, the `location` property is required, and if it is missing, an error will be thrown.
 *
 * Each path node represents a single stop on a user's journey through the app, (typically a view or "page"), and stores information about a user's decisions. This may include page settings, selections made, location information (e.g. current/previous destination IDs), and attribute data collected in each view. These data are used to construct final recommendations, or remember selections when navigating back through previous views.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} data
 * @param {string} data.location - The ID of the view the node represents.
 * @param {string} [data.prev_location] - The ID of the previous (referring) view, if one exists. Default: `''`
 * @param {array} [data.dq] - An array to hold [destination queue]{@link module:core/app.getDestinationQueue} view IDs. Default: `[]`
 * @param {object} [data.attributes] - An object where attributes of each type will be stored. Default: `{}`
 * @param {object} [data.settings] - If settings are associated with the content `location`, they will be cloned here. Default: `{}` or content settings for this location
 * @param {array} [data.selections] - Array of active input IDs that are selected in the view. If none are selected, this will be empty. Default: `[]`
 * @param {array} [data.tabs] - Array of active tab indices each each tab group. If the view has no tabs, or the tabs are not changed from initial positions, this will be empty. Default: `[]`
 *
 * @see Used by:
 * @see [**`forward`**]{@link module:core/app.forward}
 * @see [**`pathInit`**]{@link module:core/app~pathInit}
 *
 * @example
 * {
 *   location: 'usage_models',
 *   prev_location: 'start',
 *   dq: ['productivity', 'entertainment'],
 *   attributes: {
 *     app: [
 *       {
 *         name: 'platform',
 *         value: 'online',
 *       },
 *     ],
 *     filter: [
 *       {
 *         name: 'processor',
 *         value: 'i5',
 *       },
 *     ],
 *   },
 *   settings: {
 *     allow_continue: true,
 *   },
 *   selections: ['input_1_id', 'input_4_id'],
 *   tabs: [1],
 *   products: [],
 * }
 *
 * @return {object}
 */
const nodeInit = (data) => {
  // throw error if no location is provided
  if (!data.location) throw 'No node location provided.';

  // get settings from location, if they exist
  let nodeSettings = (content[data.location] && content[data.location].settings) ? Util.cloneDeep(content[data.location].settings) : {};

  // node object defaults
  let nodeDefaults = {
    prev_location: '',
    dq: [],
    attributes: {},
    settings: nodeSettings,
    selections: [],
    tabs: [],
    products: [],
  };

  // combine argument node data and node defaults
  return Object.assign(nodeDefaults, data);
};

/**
 * Initialize app [`path`]{@link module:core/app~path} with required nodes.
 *
 * The path is initialized with a start node, which contains data related to the [`start`]{@link module:core/config.start} location, as well a node "zero" that contains data related to required app functionality. This includes path attributes for app-wide settings, as well as any deep-linked primary attributes in the case of a dev start.
 *
 * [App attributes are set]{@link module:core/app.setAttributes} here for the first time.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} location - View ID of the [`start`]{@link module:core/config.start} location.
 * @param {boolean} devStart - When `true`, the querystring is checked for deep-linked primary attributes and any destination queue locations.
 * @param {boolean} shareStart - When `true`, the querystring is checked for products to populate the current node with.
 * @param {object} query - An object representing querystring parameters and their values.
 *
 * @see [`nodeInit`]{@link module:core/app~nodeInit}
 * @see [`attributesInit`]{@link module:core/app.attributesInit}
 * @see Used by:
 * @see [**`start`**]{@link module:core/app.start}
 */
const pathInit = (location, devStart, shareStart, query) => {
  // create an initial node to hold global app and/or dev start data
  // initialize node
  let nodeZero = nodeInit({ location: 'app' });

  // set up app attributes
  attributesInit(nodeZero, devStart, query);

  // add any pre-existing parameters, if dev start
  if (devStart && query.dq) {
    // make sure queue is always added as an array
    nodeZero.dq = Array.isArray(query.dq) ? query.dq : [query.dq];
    // remove queue
    delete query.dq;
  }

  // define start data
  let startData = {location};

  // if starting the app with share link products, add them to node data
  if (shareStart) startData.products = Util.decode(query.srt_share)[0].split(',');

  // create first view node
  let nodeStart = nodeInit(startData);

  // add node to path
  path = [nodeZero, nodeStart];
};

/**
 * Initialize attributes necessary for app functionality to the initial path [`node`]{@link module:core/app~nodeInit}.
 *
 * - Add [`attributes`]{@link module:core/app.attributes} based on `app.schema` attribute settings.
 * - [Customize]{@link module:core/app.customize} with project attributes.
 * - If `devStart`, use the provided querystring to add primary attributes. See [`start`]{@link module:core/app.start} for more information on dev starts.
 *
 * @since 3.2.2
 *
 * @param {object} node - Initial path node.
 * @param {boolean} devStart - Whether "dev start" mode should be used.
 * @param {object} query - An object representing querystring parameters and their values.
 *
 * @see [`customize`]{@link module:core/app.customize} `node`
 * @see Used by:
 * @see [**`pathInit`**]{@link module:core/app~pathInit}
 *
 * @example
 * // initial node
 * {
 *   location: 'app',
 *   prev_location: '',
 *   dq: [],
 *   attributes: {},
 *   settings: {},
 *   selections: [],
 *   tabs: [],
 *   products: [],
 * }
 *
 * // initial node after initializing attributes
 * {
 *   location: 'app',
 *   prev_location: '',
 *   dq: [],
 *   attributes: {
 *     app: [
 *       {
 *         name: 'platform',
 *         value: 'online',
 *       },
 *     ],
 *     filter: [
 *       {
 *         name: 'processor',
 *         value: 'i5',
 *       },
 *     ],
 *   },
 *   settings: {},
 *   selections: [],
 *   tabs: [],
 *   products: [],
 * }
 */
const attributesInit = (node, devStart, query) => {
  // add app attributes
  node.attributes.app = Object.keys(settings.app.schema).map(name => ({name, value: settings[name]}));
  // add project attributes
  customize('attributesInit', node);
  // if not dev start, stop here
  if (!devStart) return;
  // add primary attribute settings from query
  let type = settings.primary_attribute.type;
  node.attributes[type] = [];
  for (let [name, value] of Object.entries(query)) node.attributes[type].push({name, value});
};

/**
 * Return the primary [project attribute]{@link module:core/app.attributes}. This is typically the attribute that is used to determine recommendations.
 *
 * See [`updateAttributeDefs`]{@link module:core/app.updateAttributeDefs} on how to change the primary attribute.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @return {object}
 *
 * @see Used by:
 * @see [**`settingsInit`**]{@link module:core/app~settingsInit}
 * @see [**`updateAttributeDefs`**]{@link module:core/app.updateAttributeDefs}
 */
const getPrimaryAttribute = () => {
  // get primary attribute object
  let [primary] = Util.filterObjectArray(attributes, 'primary', true);
  // otherwise use the first attribute
  if (!Object.keys(primary).length) throw 'No primary attribute defined.';
  // return a copy of the attribute
  return Object.assign({}, primary);
};

/**
 * Determine if the current `lang` setting is an LTR or RTL language.
 *
 * RTL languages used in SRT include:
 * - `ar`
 * - `he`
 *
 * Returns `ltr` or `rtl`.
 *
 * @method
 *
 * @since 3.4.3
 *
 * @return {string}
 *
 * @see Used by:
 * @see [**`settingsInit`**]{@link module:core/app~settingsInit}
 */
const getLangDir = () => {
  let rtl = ['ar', 'he'];
  return rtl.includes(settings.lang) ? 'rtl' : 'ltr'
};

/**
 * Run post-navigation functions.
 *
 * - Update [`state`]{@link module:core/app.state} with current and previous [`path`]{@link module:core/app~path} nodes and last direction travelled. Note that nodes are sliced (shallow cloned) and not deep cloned, so modifying them directly will affect the `path`.
 * - [Get any requested products]{@link module:core/app~getProducts}.
 * - [Current content is updated]{@link module:core/app~setCurrentContent}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} direction - The last provided direction of travel through the app, e.g. `start`, `forward`, `back`
 *
 * @see [`updateState`]{@link module:core/app.updateState}
 * @see [`updateContent`]{@link module:core/app.updateContent}
 * @see Used by:
 * @see [**`start`**]{@link module:core/app.start}
 * @see [**`forward`**]{@link module:core/app.forward}
 * @see [**`backward`**]{@link module:core/app.backward}
 */
const postNavigate = (direction) => {
  debug(3, '%cApp path', 'font-weight:bold; color:white;', path);
  // update state
  updateState({
    path_depth: path.length - 2,
    direction,
    previous: path.slice(-2)[0],
    current: path.slice(-1)[0],
  });
  updateContent();
  // if current node includes an attribute anchor, and nav direction is not back, add past attr data as state "pre anchor" attrs
  if (currentNodeSetting('anchor_path_attributes') && direction !== 'back') {
    // copy current attributes into pre-anchor object
    let preAnchor = {
      attributes: Object.assign({}, state.attributes),
      attr: Object.assign({}, state.attr),
    };
    // add to state
    updateState({ preAnchor });
  }
};

/**
 * Prep content for use in view.
 *
 * This is the primary function for preparing view [`content`]{@link module:core/app~content} objects for use in templates. Finalized content is available to the app through the exported [`current`]{@link module:core/app.current} object, and is used to render view templates.
 *
 * An overview of the process:
 *
 * 1. Content is deep cloned from the provided content `location`. This represents the base view content. Page settings are set aside, to be added back in later.
 * 1. [Referenced content]{@link module:core/app~getReferencedContent} is gathered and added to the view content.
 * 1. If product IDs are included in the current node, [get product data]{@link module:core/app~getProductsContent} for the specified product IDs
 * 1. [Content transformations]{@link module:core/app.transformContent} are performed, including adding content location IDs and removing unused conditional content.
 * 1. Global settings are cloned to page settings, and then previously set aside page settings are added back in (overriding any global defaults).
 * 1. Current and previous location view IDs are added to view content.
 * 1. Project-specific content preparation is run using the [`customize('setCurrentContent')`]{@link module:core/app.customize} hook.
 * 1. [`current`]{@link module:core/app.current} is updated with final view content.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} location - View ID to prep content for. By default, the current [`state`]{@link module:core/app.state} location is used (`state.current.location`).
 *
 * @return {object}
 *
 * @see [`current`]{@link module:core/app.current}
 * @see [`getReferencedContent`]{@link module:core/app~getReferencedContent}
 * @see [`getProductsContent`]{@link module:core/app~getProductsContent}
 * @see [`setDTRContinue`]{@link module:core/app~setDTRContinue}
 * @see [`customize`]{@link module:core/app.customize} `view`
 * @see [`transformContent`]{@link module:core/app.transformContent}
 * @see Used by:
 * @see [**`updateContent`**]{@link module:core/app~updateContent}
 */
const setCurrentContent = (location = state.current.location) => {
  // get current view data using supplied key
  let view = getContent(location);
  // set default template, if none is specified (or provided template is not found)
  if (!view.template || !settings.templates.includes(view.template)) view.template = 'default';
  // hold content settings for the time being
  let viewSettings = view.settings || {};
  // set any referenced content
  getReferencedContent(view);
  // get products content, if necessary
  getProductsContent(view);
  // settings
  // clone app settings, then merge in page settings
  view.settings = Util.merge(Util.cloneDeep(settings), viewSettings);
  // enable DTR, if necessary
  setDTRContinue(view);
  // make current content self-aware (add current destination to current content)
  view.location = state.current.location;
  // add previous destination from view
  view.previous = state.current.prev_location || false;
  // perform any necessary modifications to view
  transformContent(view);
  // project content modifications
  customize('setCurrentContent', view);
  // update current view content
  current = view;
  debug(3, '%cCurrent content', 'font-weight:bold; color:orange;', current);
};

/**
 * Determine if direct-to-retailer (DTR) "continue" button conditions have been met, e.g. when recommendations are ready. If conditions are met, `direct_to_retailer.continue` is set to `true` in current page settings, which can be used to conditionally display DTR link (see [`View.setDTRLink`]{@link module:core/view~setDTRLink} for full example). If DTR functionality is not enabled, nothing is done.
 *
 * In order to enable the DTR link "continue" button, one or both of these values must be equal to the app's `end` view ID:
 *
 * - the current view's `destination` value
 * - the next destination ID in the destination queue
 *
 * @since 3.0.0
 *
 * @param {object} view - View content.
 *
 * @see Used by:
 * @see [**`setCurrentContent`**]{@link module:core/app~setCurrentContent}
 *
 * @example
 * // config settings or JSON settings
 * {
 *   "direct_to_retailer": {
 *     "enabled": true,
 *     "links": [ ... ]
 *   }
 *   "end": "recommend"
 * }
 *
 * @example
 * // current view content
 * {
 *   title: "Choose a brand",
 *   destination: "recommend",
 * }
 *
 * // run DTR continue check
 * setDTRContinue();
 *
 * // new view content
 * {
 *   title: "Choose a brand",
 *   destination: "recommend",
 *   settings: {
 *     direct_to_retailer: {
 *       enabled: true,
 *       continue: true,
 *       links: [ ... ],
 *     },
 *   },
 * }
 *
 * @example
 * <!-- show link in view template using updated setting -->
 * {{#if @root.settings.direct_to_retailer.continue}}
 *   <!-- DTR link here -->
 * {{/if}}
 */
const setDTRContinue = (view) => {
  // if not enabled, do nothing
  if (!settings.direct_to_retailer.enabled) return;
  // determine if DTR continue conditions are met
  if (state.current.dq[0] === settings.end || view.destination === settings.end) {
    // set page setting
    view.settings.direct_to_retailer.continue = true;
  }
};

/**
 * Process a provided view's [`ref`]{@link module:core/config.ref} content, and add referenced content into the view object (in place).
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} view - View content object
 *
 * @see [`Config.ref`]{@link module:core/config.ref}
 * @see Used by:
 * @see [**`setCurrentContent`**]{@link module:core/app~setCurrentContent}
 */
const getReferencedContent = (view) => {
  // if no ref object exists, do nothing
  if (!view.ref) return;
  // copy referenced content into object
  for (let [property, refID] of Object.entries(view.ref)) {
    // if array of ids are provided, attempt to add content from all referenced view IDs
    // only works for array-type content
    if (Array.isArray(refID)) view[property] = refID.reduce((a, id) => Array.isArray(content[id][property]) ? a.concat(Util.cloneDeep(content[id][property])) : a, []);
    // otherwise add content directly
    else view[property] = Util.cloneDeep(content[refID][property]);
  }
  // remove ref
  delete view.ref;
};

/**
 * Add product data to view content.
 *
 * - For any product IDs exist in the current node (gathered by [`getProducts`]{@link module:core/app~getProducts}), [product objects are retrieved]{@link module:core/filter.getProductsByID} for each ID.
 * - If an analytics session ID is present, check each product for a Hatch product link, and append `guid=<session_id>` parameter to it.
 * - Product metadata is cloned into view content as `productMeta`, and [applied to products]{@link module:core/app~setProductsMeta}.
 * - Any provided [products content]{@link module:core/config._content_type_products} is [cloned into each product object]{@link module:core/app~setProductContent}.
 * - Products are added to the view under the `products` property, up to the current request quantity provided in product meta.
 * - If the number of products exceeds the current request quantity provided in product meta, any excess products are added to the view under the `productsShowMore` property. These products are grouped into sub-arrays, each of which are the length of the current `show_more_products.increment` [view setting]{@link module:core/config.view}.
 *
 * @method
 *
 * @since 3.1.0
 *
 * @param {object} view - View content object
 *
 * @see [`Filter.getProductsByID`]{@link module:core/filter.getProductsByID}
 * @see [`setProductsMeta`]{@link module:core/app~setProductsMeta}
 * @see [`setProductContent`]{@link module:core/app~setProductContent}
 * @see Used by:
 * @see [**`setCurrentContent`**]{@link module:core/app~setCurrentContent}
 */
const getProductsContent = (view) => {
  // do nothing of no product ids exist
  if (!state.current.products.length) return;
  // clone product objects from product ids
  let products = Util.cloneDeep(Filter.getProductsByID(...state.current.products));
  // clone product metadata to view content
  view.productMeta = Util.cloneDeep(state.current.productMeta);
  // set meta for current view products
  setProductsMeta(products, view.productMeta);
  // add product content type data to each product
  products = setProductContent(view, products);
  // if we have an analytics session id
  if (settings.analytics.session_id) {
    // check products for gethatch urls and append guid
    for (let product of products) {
      if (product.link && product.link.includes('https://gethatch.com')) product.link += `&guid=${settings.analytics.session_id}`;
    }
  }
  // if products length is longer than request quantity, split extra products off and separate into arrays for use with "show more" functionality
  if (view.productMeta.requestQuantity > 0 && view.productMeta.requestQuantity < products.length) {
    // remove initial request amount from products and add to view
    view.products = products.splice(0, view.productMeta.requestQuantity);
    // if a show more limit exists, and remaining products exceed it, slice remaining products to that length
    let limit = settings.view.show_more_products.limit;
    if (limit > 0 && products.length > limit) products = products.slice(0, limit);
    // create array in which each element is an array of products, each the length of the "show more" increment value
    view.productsShowMore = [];
    let increment = settings.view.show_more_products.increment;
    while (products.length > increment) view.productsShowMore.push(products.splice(0, increment));
    view.productsShowMore.push(products);
  }
  // otherwise just products to view content
  else view.products = products;
};

/**
 * Provided [product content]{@link module:core/config._content_type_products} is cloned into each product object as `content`. The original source object is deleted.
 *
 * @param {object} view - Current view data.
 *
 * @param {array} products - Products to modify or add content to.
 *
 * @returns {array}
 *
 * @see [`customize`]{@link module:core/app.customize} `view` `products`
 * @see Used by:
 * @see [**`getProductsContent`**]{@link module:core/app~getProductsContent}
 */
const setProductContent = (view, products) => {
  // get products content index
  const contentIndex = Util.indexOfObjectByProperty('content_type', 'products', view.content);
  // process product content, if present
  if (contentIndex >= 0 && view.content[contentIndex].products?.content) {
    // get product content
    const content = view.content[contentIndex].products.content;
    // inject any additional product content into each product
    products = products.map(product => Object.assign(product, {content: Util.cloneDeep(content)}));
    // remove content source
    delete view.content[contentIndex].products.content;
    // customize
    customize('setProductContent', view, products);
  }
  // return products
  return products;
};

/**
 * Set metadata for product results. The following data is added as properties to a `product.meta` object:
 *
 * - `uniformResults` - `true` if all products are primary results.
 * - `primary` - `true` if product is a primary result, `false` if not
 * - `firstOfType` - `true` if product is the first product in the primary or secondary group, `false` if not.
 * - `lastOfType` - `true` if product is the last product in the primary or secondary group, `false` if not.
 *
 * **Note that if the current page is a "view all" page, an empty object is stored.**
 *
 * @param {array} products - Products to set metadata for.
 *
 * @param {object} meta - Product results metadata.
 *
 * @see [`isViewAll`]{@link module:core/app.isViewAll}
 * @see Used by:
 * @see [**`getProductsContent`**]{@link module:core/app~getProductsContent}
 */
const setProductsMeta = (products, meta) => {
  let uniformResults = meta.requestQuantity < products.length;
  // loop through products
  for (let i = 0; i < products.length; i++) {
    // if in a view all page, empty product meta, otherwise add data
    products[i].meta = isViewAll() ? {} : {
      uniformResults,
      primary: i < meta.primaryCount,
      firstOfType: i === 0 || i === meta.primaryCount,
      lastOfType: i === meta.primaryCount - 1 || i === products.length -1,
    }
  }
}

/**
 * Compile all attributes from a specified type from the [`path`]{@link module:core/app~path}.
 *
 * All path nodes are checked for attributes of the specified type, unless a node with an `anchor_path_attributes` [setting]{@link module:core/config.page_settings} is encountered, in which case attribute lookup will stop. *Note that this before can be circumvented per [`attribute`]{@link module:core/app.attributes} type using the `ignoreAnchor` property.*
 *
 * Returns an array of uncondensed attributes objects.
 *
 * @since 3.0.0
 *
 * @param {object} options - Options for retrieving attributes. Properties mirror [`attribute`]{@link module:core/app.attributes} definitions.
 * @param {string} options.type - Type of attribute to retrieve.
 * @param {boolean} options.ignoreAnchor - Whether or not attribute anchors should be ignored. Default: `false`
 *
 * @return {array}
 *
 * @see Used by:
 * @see [**`getAttributesFrom`**]{@link module:core/app.getAttributesFrom}
 *
 * @example
 * // path nodes with attributes
 * [
 *   {
 *     attributes: {
 *       filter: [
 *         {
 *           name: 'processor',
 *           value: 'i5',
 *           priority: 3,
 *         },
 *       ],
 *     },
 *   },
 *   {
 *     attributes: {
 *       filter: [
 *         {
 *           name: 'processor',
 *           value: 'i7',
 *           priority: 2,
 *         },
 *         {
 *           name: 'storage_amount',
 *           value: 500,
 *         },
 *       ],
 *     },
 *   },
 * ]
 *
 * // gather attributes
 * pathAttributes({type: 'filter'});
 *
 * // expected result
 * [
 *   {
 *     name: 'processor',
 *     value: 'i5',
 *     priority: 3,
 *   },
 *   {
 *     name: 'processor',
 *     value: 'i7',
 *     priority: 2,
 *   },
 *   {
 *     name: 'storage_amount',
 *     value: 500,
 *   },
 * ]
 */
const pathAttributes = (options = {}) => {
  // hold found attribute objects
  let attributes = [];

  // copy and reverse path to work backwards through it
  let workingPath = path.slice().reverse();

  for (let i = 0; i < workingPath.length; i++) {
    let node = workingPath[i];
    // check if attributes of the desired type were added to this node
    let nodeAttributes = node.attributes[options.type];
    // if node has attributes for this type, add them to final attributes array
    if (nodeAttributes && nodeAttributes.length) attributes.push(...nodeAttributes);

    // if the anchor_path_attributes is enabled, and this attribute type does not ignore anchors, don't check any further
    if (!options.ignoreAnchor && node.settings.anchor_path_attributes) break;
  }

  // revert order of attributes before return
  attributes.reverse();

  return attributes;
};

/**
 * Determine if current page settings require products, and if so, process recommendations with any project-specific filter options. An overview of this process:
 *
 * 1. Check current node [setting]{@link module:core/config.page_settings} `get_products` for a products recommendation request. If no products are requested, or products already exist in the current node, the function stops here. An exception to this is if the `reload_products_on_state_change` [view setting]{@link module:core/config.view} is enabled, in which case products will always be replaced.
 * 2. Create `filterObject` to hold filter options
 * 3. Copy condensed primary attributes to `filterObject.filter_on`
 * 4. Set the current `get_products` value to `filterObject.quantity`
 * 5. Add any project-specific filter options using [`customize('getProducts')`]{@link module:core/app.customize}
 * 6. Run [`Filter.recommend()`]{@link module:core/filter.recommend} with `meta` arg enabled to get an object containing product IDs and any related metadata. These are our recommended products.
 * 7. Store product data in current node.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`customize`]{@link module:core/app.customize} `filterRequest`
 * @see [`Filter.recommend`]{@link module:core/filter.recommend}
 * @see [`updatePathNode`]{@link module:core/app.updatePathNode}
 * @see Used by:
 * @see [**`updateContent`**]{@link module:core/app~updateContent}
 *
 * @example
 * // view / node settings
 * {
 *   get_products: 3
 * }
 *
 * // get recommended products
 * getProducts();
 *
 * // example result, which is stored in current node
 * {
 *   products: ['product_04', 'product_36', 'product_16'],
 *   productMeta: {
 *     paddedIDs: ['product_16']
 *   }
 * }
 */
const getProducts = () => {
  let quantity = currentNodeSetting('get_products');
  // if no products are needed, or products already exist in current node, do nothing
  // if reloading products, existing products will be disregarded
  if (!quantity || (state.current.products.length && !settings.view.reload_products_on_state_change)) return;
  // create filters object
  let filterRequest = {};
  // get active filters
  filterRequest.filter_on = Object.assign({}, state.attr[settings.primary_attribute.type]);
  // set limit
  filterRequest.quantity = quantity;
  // allow for any product modifications
  customize('getProducts', filterRequest);
  // add product IDS and product meta to node
  updatePathNode(Filter.recommend(filterRequest, true, true));
};

/**
 * Update persistent settings data in IndexedDB database.
 *
 * If any settings keys are specified in the `persistent` [app setting]{@link module:core/config.app_settings}, the current config value for that setting key is gathered and added to an object, which is [stored in the database]{@link module:core/app.db.put} under the `config` keyPath.
 *
 * @method
 *
 * @since 3.2.4
 *
 * @see [`db.put`]{@link module:core/app.db.put}
 * @see Used by:
 * @see [**`updateSettings`**]{@link module:core/app.updateSettings}
 */
const updatePersistentSettings = () => {
  // if persistent settings are not enabled, do nothing
  if (!settings.persistent.enabled) return;
  // hold persistent settings
  let persistent = {};
  // loop through persistent settings paths
  for (let setting of settings.persistent.paths) {
    // get value of setting
    let value = Util.getObjectValue(settings, setting, '.', true);
    // if setting was found, merge into persistent object
    if (typeof value !== 'undefined') Util.merge(persistent, value);
  }
  // if we have any persistent keys to update, update them in database
  if (Object.keys(persistent).length) db.put(persistent).catch((e) => Util.logError(e, 'Unable to store configuration.'));
};
